<template>
  <main>
    <!-- ================= TABS ================= -->
    <div
      class="tabs-custom"
      v-if="currentTab !== 'captura' && currentTab !== 'feedback'"
    >
      <button
        class="tab-custom"
        :class="{ active: currentTab === 'actual' }"
        @click="showTab('actual')"
      >
        Encuestas Actual
      </button>
      <button
        class="tab-custom"
        :class="{ active: currentTab === 'hist' }"
        @click="showTab('hist')"
      >
        Encuestas Histórico
      </button>
      <button
        class="tab-custom"
        :class="{ active: currentTab === 'dir' }"
        @click="showTab('dir')"
      >
        Directorio CX
      </button>
    </div>

    <!-- ================= VISTA ENCUESTAS ACTUAL ================= -->
    <section v-if="currentTab === 'actual'" class="view-actual">
      <!-- TOOLBAR: cliente + acciones -->
      <div class="actual-toolbar">
        <div class="toolbar-field">
          <label for="selCuenta">Cliente</label>
          <select
            id="selCuenta"
            v-model="cuentaSeleccionada"
            @change="onSelectCuenta"
          >
            <option value="">— Selecciona un cliente —</option>
            <option
              v-for="c in cuentas"
              :key="c.idCuenta || c.idcuenta"
              :value="c.idCuenta || c.idcuenta"
            >
              {{ c.nombre }}
            </option>
          </select>
        </div>
        <div class="toolbar-actions">
          <!-- Análisis con IA: acción secundaria, discreta pero reconocible -->
          <button
            v-if="dashboardData && dashboardData.tieneDatos !== 'N'"
            class="btn-ai"
            type="button"
            :disabled="analysisLoading"
            title="Genera con IA un resumen ejecutivo de la encuesta: hallazgos, riesgos y recomendaciones"
            aria-describedby="btnAiDesc"
            @click="generarAnalisis('actual')"
          >
            <svg class="btn-ai-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z"
              />
              <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z" />
            </svg>
            {{ analysisLoading ? "Analizando…" : "Análisis ejecutivo" }}
            <span class="btn-ai-badge">IA</span>
          </button>
          <span id="btnAiDesc" class="sr-only">
            Genera con inteligencia artificial un resumen ejecutivo de la
            encuesta con hallazgos, riesgos y recomendaciones.
          </span>
          <button
            class="btn-custom"
            :disabled="!cuentaSeleccionada"
            @click="openCapture"
          >
            + Capturar encuesta
          </button>
        </div>
      </div>

      <div
        v-if="dashboardData && dashboardData.tieneDatos !== 'N'"
        class="actual-stack"
      >
        <!-- HERO -->
        <div class="grid hero">
          <div class="card-custom gauge-card reveal">
            <div class="label-custom">Calificación ponderada global</div>
            <div class="gauge-svg" v-html="gaugeSVG"></div>
            <div class="gauge-num">{{ formattedScore }}<small>%</small></div>
            <div class="status-chip" :style="statusChipStyle">
              ● {{ dashboardData.semaforo || dashboardData.estatus || "—" }}
            </div>
            <div class="gauge-cap">
              <b>{{ dashboardData.cliente || "" }}</b
              ><br />
              Última aplicación:
              <span>{{ dashboardData.fechaAplicacion || "—" }}</span>
            </div>
          </div>

          <div class="card-custom reveal" style="animation-delay: 0.1s">
            <div class="sec-title">
              <span class="bar-accent"></span>Indicadores CX
            </div>
            <div class="sec-sub">Métricas clave de experiencia del cliente</div>
            <div class="grid kpi-grid">
              <div v-for="kpi in kpiList" :key="kpi.name" class="kpi">
                <svg width="104" height="104" viewBox="0 0 108 108">
                  <circle
                    cx="54"
                    cy="54"
                    r="42"
                    fill="none"
                    stroke="rgba(0,0,0,0.08)"
                    stroke-width="9"
                  />
                  <circle
                    cx="54"
                    cy="54"
                    r="42"
                    fill="none"
                    :stroke="kpi.color"
                    stroke-width="9"
                    stroke-linecap="round"
                    :stroke-dasharray="circleCircumference"
                    :stroke-dashoffset="kpi.offset"
                    transform="rotate(-90 54 54)"
                    class="kpi-ring"
                  />
                  <text
                    x="54"
                    y="50"
                    text-anchor="middle"
                    fill="var(--txt)"
                    class="kpi-value-text"
                  >
                    {{ kpi.displayValue }}
                  </text>
                  <text
                    x="54"
                    y="68"
                    text-anchor="middle"
                    fill="var(--txt-faint)"
                    class="kpi-label-text"
                  >
                    /100
                  </text>
                </svg>
                <h3>{{ kpi.name }}</h3>
                <div class="desc">{{ kpi.desc }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- CATEGORÍAS -->
        <div class="card-custom reveal" style="animation-delay: 0.15s">
          <div class="sec-title">
            <span class="bar-accent"></span>Desempeño por categoría
          </div>
          <div class="sec-sub">Calificación obtenida por aspecto evaluado</div>
          <div>
            <div
              v-for="cat in categoriasFiltradas"
              :key="cat.seccion"
              class="cat-row"
            >
              <div class="cat-top">
                <span class="cat-name">{{ cat.seccion }}</span>
                <span
                  class="cat-val"
                  :style="{ color: getColor(cat.semaforo) }"
                >
                  {{ cat.score || "—" }}%
                </span>
              </div>
              <div class="bar">
                <span
                  class="bar-fill"
                  :style="{
                    width: barWidth(cat.score),
                    background: barGradient(cat.semaforo),
                  }"
                ></span>
              </div>
            </div>
          </div>
        </div>

        <!-- INSIGHT AUTOMÁTICO -->
        <div class="card-custom insight reveal" style="animation-delay: 0.2s">
          <div class="ic">💡</div>
          <div>
            <h4>Insight automático</h4>
            <p v-html="insightText"></p>
          </div>
        </div>

        <!-- CONTACTO DE LA CUENTA -->
        <div
          v-if="contactoDashboard"
          class="card-custom reveal"
          style="animation-delay: 0.15s"
        >
          <div class="sec-title">
            <span class="bar-accent"></span>Contacto de la cuenta
          </div>
          <div class="sec-sub">Información del contacto asociado</div>
          <div>
            <div class="contact-info-grid">
              <div>
                <span class="contact-label">👤 Nombre:</span>
                <strong>{{ contactoDashboard.nombre || "—" }}</strong>
              </div>
              <div>
                <span class="contact-label">💼 Puesto:</span>
                <strong>{{ contactoDashboard.puesto || "—" }}</strong>
              </div>
              <div>
                <span class="contact-label">🏢 Cuenta:</span>
                <strong>{{ contactoDashboard.cuenta || "—" }}</strong>
              </div>
              <div>
                <span class="contact-label">✉️ Email:</span>
                <strong>{{ contactoDashboard.email || "—" }}</strong>
              </div>
              <div style="grid-column: 1/-1">
                <span class="contact-label">📞 Teléfono:</span>
                <strong>{{ contactoDashboard.telefono || "—" }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- FEEDBACK DASHBOARD -->
        <div
          v-if="feedbackItems && feedbackItems.length"
          class="card-custom reveal"
          style="animation-delay: 0.25s"
        >
          <div class="sec-title">
            <span class="bar-accent feedback-accent"></span>
            🏷️ Feedback interno del equipo
            <span class="feedback-count">
              <span class="dotlive"></span>
              {{ feedbackItems.length }} comentarios
            </span>
          </div>
          <div class="sec-sub">
            Comentarios internos sobre el desempeño por sección
          </div>
          <div>
            <div
              v-for="item in feedbackItems"
              :key="item.seccion"
              class="feedback-item"
            >
              <div class="feedback-item-header">
                📋 {{ item.seccion || "Sin sección" }}
              </div>
              <div class="feedback-item-body">
                {{ item.feedback || "—" }}
              </div>
              <div class="feedback-item-date">
                {{ item.fecha || "" }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="cuentaSeleccionada" class="empty">
        Este cliente aún no tiene encuestas capturadas.<br />Usa
        <b>+ Capturar encuesta</b> para registrar la primera.
      </div>
      <div v-else class="empty">
        Selecciona un cliente para ver su dashboard.
      </div>
    </section>

    <!-- ================= VISTA CAPTURA ================= -->
    <section v-if="currentTab === 'captura'">
      <div
        class="row"
        style="justify-content: space-between; margin-bottom: 12px"
      >
        <div>
          <div class="muted" style="font-size: 12px">
            Capturando encuesta para
          </div>
          <div class="section-main-title">{{ clienteNombre }}</div>
          <div class="muted" style="font-size: clamp(13px, 1.1vw, 15px)">
            {{ encuestaNombre }}
          </div>
        </div>
        <button class="btn-custom secondary" @click="cancelarCaptura">
          ← Cancelar
        </button>
      </div>

      <!-- ===== PASO 1: Período + selección de secciones ===== -->
      <div v-if="capturaStep === 'secciones'">
        <div class="sec" style="margin-top: 12px">
          <h3>Período de la encuesta</h3>
          <div class="w">Indica el mes y año al que corresponde la captura</div>
          <div class="row" style="margin-top: 10px">
            <div style="max-width: 200px">
              <label for="capMes">Mes *</label>
              <select id="capMes" v-model.number="capturaMes">
                <option v-for="m in 12" :key="m" :value="m">
                  {{ mesesNombres[m - 1] }}
                </option>
              </select>
            </div>
            <div style="max-width: 200px">
              <label for="capAnio">Año *</label>
              <select id="capAnio" v-model.number="capturaAnio">
                <option v-for="y in aniosCaptura" :key="y" :value="y">
                  {{ y }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="sec" style="margin-top: 12px">
          <h3>¿Qué secciones deseas activar en la encuesta?</h3>
          <div class="w">
            Solo se mostrarán y guardarán las secciones seleccionadas
          </div>
          <div class="secciones-check">
            <label
              v-for="s in seccionesDisponibles"
              :key="s"
              class="check-chip"
              :class="{ active: seccionesSeleccionadas.includes(s) }"
            >
              <input
                type="checkbox"
                :checked="seccionesSeleccionadas.includes(s)"
                @change="toggleSeccion(s)"
              />
              <span>{{ s }}</span>
            </label>
          </div>
        </div>

        <div class="row" style="justify-content: flex-end; margin-top: 8px">
          <button class="btn-custom" @click="continuarCaptura">
            Continuar →
          </button>
        </div>
      </div>

      <!-- ===== PASO 2: Formulario de la encuesta ===== -->
      <div v-if="capturaStep === 'form'">
      <div class="row" style="margin-bottom: 8px">
        <button class="btn-custom secondary" @click="capturaStep = 'secciones'">
          ← Secciones y período
        </button>
        <div class="muted" style="font-size: 12px; align-self: center">
          Período: {{ String(capturaMes).padStart(2, "0") }}/{{ capturaAnio }}
        </div>
      </div>

      <!-- Selector de contacto -->
      <div class="sec" style="margin-top: 12px">
        <h3>Selecciona contacto de la cuenta</h3>
        <div style="margin-top: 8px">
          <label for="dirContacto">Contacto</label>
          <select
            id="dirContacto"
            v-model="contactoSeleccionadoId"
            @change="onContactoChange"
          >
            <option value="">Selecciona un contacto...</option>
            <option
              v-for="c in contactosFiltrados"
              :key="c.idContacto || c.idcontacto"
              :value="c.idContacto || c.idcontacto"
            >
              {{ c.nombre || "Sin nombre" }}
            </option>
          </select>
        </div>

        <!-- Tarjeta de contacto seleccionado -->
        <div v-if="contactoSeleccionadoData" class="contact-card-info">
          <div class="contact-card-title">📋 Contacto seleccionado</div>
          <div class="contact-info-grid">
            <div>
              <span class="contact-label">Nombre:</span>
              <span class="contact-value">{{
                contactoSeleccionadoData.nombre || "—"
              }}</span>
            </div>
            <div>
              <span class="contact-label">Puesto:</span>
              <span class="contact-value">{{
                contactoSeleccionadoData.puesto || "—"
              }}</span>
            </div>
            <div>
              <span class="contact-label">Cuenta:</span>
              <span class="contact-value">{{
                contactoSeleccionadoData.cuenta || "—"
              }}</span>
            </div>
            <div>
              <span class="contact-label">Email:</span>
              <span class="contact-value">{{
                contactoSeleccionadoData.email || "—"
              }}</span>
            </div>
            <div style="grid-column: 1/-1">
              <span class="contact-label">Teléfono:</span>
              <span class="contact-value">{{
                contactoSeleccionadoData.telefono || "—"
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Formulario de encuesta (solo secciones activas) -->
      <div id="capForm">
        <div
          v-for="seccion in seccionesActivas"
          :key="seccion.id || seccion.seccion"
          class="sec"
        >
          <h3>{{ seccion.seccion }}</h3>
          <div class="w">Peso de sección: {{ seccion.pesoSeccion || "—" }}</div>

          <!-- Indicadores clave (0–10) integrados en la sección KPIS CX -->
          <div v-if="esSeccionKpi(seccion)" class="kpis-mini" style="margin-top: 10px">
            <div v-for="kpi in kpiSelectors" :key="kpi.id" class="kpi-selector">
              <label>{{ kpi.label }} (0–10)</label>
              <div class="kpi-btn-group">
                <button
                  v-for="i in 11"
                  :key="i - 1"
                  class="btn-option kpi-color"
                  :class="{ selected: kpi.value === i - 1 }"
                  :style="kpiBtnStyle(i - 1, kpi.value === i - 1)"
                  @click="kpi.value = i - 1"
                >
                  {{ i - 1 }}
                </button>
              </div>
            </div>
          </div>

          <div
            v-for="pregunta in seccion.preguntas"
            :key="pregunta.idPregunta"
            class="q"
          >
            <p>{{ pregunta.numPregunta }}. {{ pregunta.texto }}</p>
            <div v-if="pregunta.tipo === 'ABIERTA'" class="opts">
              <textarea
                rows="2"
                :placeholder="`Comentario para ${pregunta.texto} (opcional)`"
                v-model="respuestas[pregunta.idPregunta]"
              ></textarea>
            </div>
            <div v-else class="opts">
              <label
                v-for="opcion in pregunta.opciones"
                :key="opcion.idOpcion"
                class="radio-label"
              >
                <input
                  type="radio"
                  :name="'p' + pregunta.idPregunta"
                  :value="opcion.idOpcion"
                  v-model="respuestas[pregunta.idPregunta]"
                />
                {{ opcion.etiqueta }}
                <span class="muted">({{ opcion.valor }})</span>
              </label>
            </div>
          </div>

          <div class="q sec-coment">
            <p>Comentarios de la sección (opcional)</p>
            <textarea
              rows="5"
              maxlength="4000"
              :placeholder="`Comentarios sobre ${seccion.seccion} (opcional, máx. 4000 caracteres)`"
              v-model="comentariosSeccion[seccion.seccion]"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="row" style="justify-content: flex-end; margin-top: 8px">
        <button
          class="btn-custom"
          :disabled="loadingGuardar"
          @click="guardarEncuesta"
        >
          <span v-if="loadingGuardar" class="spinner-sm"></span>
          {{ loadingGuardar ? "Guardando…" : "Guardar encuesta" }}
        </button>
      </div>
      </div>
      <!-- /Paso 2 -->
    </section>

    <!-- ================= VISTA FEEDBACK ================= -->
    <section v-if="currentTab === 'feedback'">
      <div
        class="row"
        style="justify-content: space-between; margin-bottom: 12px"
      >
        <div>
          <div class="muted" style="font-size: 12px">Feedback interno para</div>
          <div class="section-main-title">{{ clienteNombre }}</div>
          <div class="muted" style="font-size: clamp(13px, 1.1vw, 15px)">
            {{ encuestaNombre }}
          </div>
          <div class="muted" style="font-size: 12px; margin-top: 4px">
            Ejecución #{{ feedbackEjecucionId }}
          </div>
        </div>
        <div style="display: flex; gap: 8px; flex-wrap: wrap">
          <button class="btn-custom secondary" @click="saltarFeedback">
            Omitir feedback
          </button>
        </div>
      </div>

      <div class="feedback-info-banner">
        <div class="feedback-info-title">💬 Feedback interno del equipo</div>
        <div class="feedback-info-text">
          Agrega comentarios internos sobre el desempeño de cada sección. Esta
          información es solo para uso interno del equipo.
        </div>
      </div>

      <div id="feedbackForm">
        <div
          v-for="seccion in seccionesActivas"
          :key="seccion.id || seccion.seccion"
          class="feedback-section"
        >
          <div class="section-header">
            <h4>📋 {{ seccion.seccion }}</h4>
            <span
              class="score-badge"
              :style="{
                color: getColorByScore(seccion.score || 0),
                background: hexaColor(getColorByScore(seccion.score || 0), 0.1),
                border:
                  '1px solid ' +
                  hexaColor(getColorByScore(seccion.score || 0), 0.2),
              }"
            >
              Score:
              {{ seccion.score != null ? seccion.score + "%" : "Sin evaluar" }}
            </span>
          </div>
          <textarea
            class="feedback-textarea"
            :placeholder="`Escribe tu feedback interno sobre la sección ${seccion.seccion} (opcional)`"
            maxlength="4000"
            rows="3"
            v-model="feedbackData[seccion.seccion]"
          ></textarea>
          <div class="feedback-counter">
            <span>{{ (feedbackData[seccion.seccion] || "").length }}</span
            >/4000 caracteres
          </div>
        </div>
      </div>

      <div
        class="row"
        style="justify-content: flex-end; margin-top: 16px; gap: 12px"
      >
        <button class="btn-custom secondary" @click="cancelarFeedback">
          ← Cancelar
        </button>
        <button class="btn-custom" @click="guardarFeedback">
          💾 Guardar feedback
        </button>
      </div>
    </section>

    <!-- ================= VISTA ENCUESTAS HISTÓRICO ================= -->
    <section v-if="currentTab === 'hist'">
      <!-- Breadcrumb mejorado -->
      <div class="crumbs">
        <template v-if="histLevel === 'filtros'">
          <span class="cur">Encuestas Histórico</span>
        </template>
        <template v-else-if="histLevel === 'lista'">
          <a @click="histLevel = 'filtros'">Filtros</a>
          <span class="sep">›</span>
          <span class="cur">{{ histBreadcrumbText }}</span>
        </template>
        <template v-else-if="histLevel === 'detalle'">
          <a @click="histLevel = 'filtros'">Filtros</a>
          <span class="sep">›</span>
          <a @click="histLevel = 'lista'">{{ histBreadcrumbText }}</a>
          <span class="sep">›</span>
          <span class="cur">Ejecución</span>
        </template>
      </div>

      <!-- Nivel 1 · Filtros -->
      <div v-if="histLevel === 'filtros'">
        <div class="filters">
          <div>
            <label for="hSelCuenta">Cliente</label>
            <select id="hSelCuenta" v-model="histFiltroCuenta">
              <option value="">— Selecciona —</option>
              <option
                v-for="c in cuentas"
                :key="c.idCuenta || c.idcuenta"
                :value="c.idCuenta || c.idcuenta"
              >
                {{ c.nombre }}
              </option>
            </select>
          </div>
          <div>
            <label for="hSelEncuesta">Encuesta</label>
            <select id="hSelEncuesta" v-model="histFiltroEncuesta">
              <option value="">— Selecciona —</option>
              <option v-if="encuestaData" :value="encuestaData.idEncuesta">
                {{ encuestaData.nombre }} · {{ encuestaData.clave }} v{{
                  encuestaData.version
                }}
              </option>
            </select>
          </div>
          <div>
            <label for="hDesde">Fecha desde (opcional)</label>
            <input type="date" id="hDesde" v-model="histFiltroFecha" />
          </div>
          <div style="flex: 0 0 auto">
            <button
              class="btn-custom"
              :disabled="loadingHist"
              @click="consultarHistorico"
            >
              <span v-if="loadingHist" class="spinner-sm"></span>
              {{ loadingHist ? "Consultando…" : "Consultar" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Nivel 2 · Lista de ejecuciones -->
      <div v-if="histLevel === 'lista'">
        <div
          class="row"
          style="justify-content: space-between; margin-bottom: 12px"
        >
          <div class="sec-title">
            <span class="bar-accent"></span>Ejecuciones históricas
          </div>
          <button class="btn-custom secondary" @click="histLevel = 'filtros'">
            ← Volver a filtros
          </button>
        </div>
        <div class="exec-list">
          <div
            v-for="exec in historicoEjecuciones"
            :key="exec.idEjecucion || exec.idejecucion"
            class="exec-row"
            @click="abrirEjecucion(exec.idEjecucion || exec.idejecucion)"
          >
            <div class="exec-left">
              <span class="exec-client">{{
                exec.cliente || exec.nombreCliente || exec.CLIENTE || "—"
              }}</span>
              <span
                v-if="exec.nombreContacto || exec.nombrecontacto"
                class="exec-contacto"
              >
                👤 {{ exec.nombreContacto || exec.nombrecontacto }}
              </span>
              <span class="exec-date">{{
                exec.fechaAplicacion || exec.fechaaplicacion || "—"
              }}</span>
              <span class="exec-sub"
                >{{ exec.encuestaClave || "" }} v{{
                  exec.encuestaVersion || ""
                }}
                · {{ exec.periodo || "" }}</span
              >
            </div>
            <div class="exec-right">
              <span class="exec-kpis"
                >NPS {{ exec.nps || "—" }} · CES {{ exec.ces || "—" }} · CSAT
                {{ exec.csat || "—" }}</span
              >
              <span
                class="pill-sem"
                :style="{
                  color: getColor(exec.semaforo || exec.SEMAFORO),
                  background: hexaColor(
                    getColor(exec.semaforo || exec.SEMAFORO),
                    0.12,
                  ),
                  borderColor: hexaColor(
                    getColor(exec.semaforo || exec.SEMAFORO),
                    0.3,
                  ),
                }"
                >● {{ exec.semaforo || exec.SEMAFORO || "—" }}</span
              >
              <span
                class="exec-score"
                :style="{
                  color: getColor(exec.semaforo || exec.SEMAFORO),
                }"
              >
                {{
                  exec.scorePct ?? exec.scorepct
                    ? Math.round(getScorePct(exec) * 10) / 10
                    : "—"
                }}%
              </span>
              <div class="exec-actions">
                <button
                  v-if="puedeEditar"
                  class="icon-btn"
                  title="Editar ejecución"
                  @click.stop="abrirEditar(exec)"
                >
                  ✏️
                </button>
                <button
                  v-if="esAdmin"
                  class="icon-btn danger"
                  title="Eliminar ejecución"
                  @click.stop="abrirEliminar(exec)"
                >
                  🗑️
                </button>
              </div>
              <span class="exec-chevron">›</span>
            </div>
          </div>
        </div>
        <div v-if="loadingHist" class="empty">
          <span class="spinner-sm dark"></span> Cargando ejecuciones…
        </div>
        <div
          v-else-if="!historicoEjecuciones.length"
          class="empty"
        >
          No hay ejecuciones para los filtros seleccionados.
        </div>
      </div>

      <!-- Nivel 3 · Detalle de ejecución -->
      <div v-if="histLevel === 'detalle' && historicoDetalle">
        <div class="header-actions">
          <button
            class="btn-custom"
            :disabled="analysisLoading"
            @click="generarAnalisis('hist')"
          >
            <span v-if="analysisLoading" class="spinner-sm"></span>
            🧠 Análisis Ejecutivo
          </button>
          <button class="btn-custom secondary" @click="exportarPDF">
            📄 Exportar a PDF
          </button>
        </div>
        <div
          class="row"
          style="justify-content: space-between; margin-bottom: 12px"
        >
          <div class="sec-title">
            <span class="bar-accent"></span>Detalle de ejecución
          </div>
          <button class="btn-custom secondary" @click="histLevel = 'lista'">
            ← Volver a la lista
          </button>
        </div>

        <div class="grid hero">
          <div class="card-custom gauge-card">
            <div class="label-custom">Calificación ponderada global</div>
            <div class="gauge-svg" v-html="historicoGaugeSVG"></div>
            <div class="gauge-num">
              {{ formattedHistScore }}<small>%</small>
            </div>
            <div class="status-chip" :style="historicoStatusChipStyle">
              ● {{ historicoDetalle.semaforo || "—" }}
            </div>
            <div class="gauge-cap">
              <b>{{ historicoDetalle.cliente || "" }}</b
              ><br />
              <span class="muted">{{ historicoEncuestaMeta }}</span
              ><br />
              Aplicación:
              <span>{{ historicoDetalle.fechaAplicacion || "—" }}</span>
            </div>
          </div>
          <div class="card-custom">
            <div class="sec-title">
              <span class="bar-accent"></span>Indicadores CX
            </div>
            <div class="sec-sub">Métricas clave de experiencia del cliente</div>
            <div class="grid kpi-grid">
              <div v-for="kpi in historicoKpiList" :key="kpi.name" class="kpi">
                <svg width="104" height="104" viewBox="0 0 108 108">
                  <circle
                    cx="54"
                    cy="54"
                    r="42"
                    fill="none"
                    stroke="rgba(0,0,0,0.08)"
                    stroke-width="9"
                  />
                  <circle
                    cx="54"
                    cy="54"
                    r="42"
                    fill="none"
                    :stroke="kpi.color"
                    stroke-width="9"
                    stroke-linecap="round"
                    :stroke-dasharray="circleCircumference"
                    :stroke-dashoffset="kpi.offset"
                    transform="rotate(-90 54 54)"
                    class="kpi-ring"
                  />
                  <text
                    x="54"
                    y="50"
                    text-anchor="middle"
                    fill="var(--txt)"
                    class="kpi-value-text"
                  >
                    {{ kpi.displayValue }}
                  </text>
                  <text
                    x="54"
                    y="68"
                    text-anchor="middle"
                    fill="var(--txt-faint)"
                    class="kpi-label-text"
                  >
                    /100
                  </text>
                </svg>
                <h3>{{ kpi.name }}</h3>
                <div class="desc">{{ kpi.desc }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Categorías -->
        <div class="card-custom" style="margin-top: 16px">
          <div class="sec-title">
            <span class="bar-accent"></span>Desempeño por categoría
          </div>
          <div class="sec-sub">Calificación obtenida por aspecto evaluado</div>
          <div style="margin-top: 6px">
            <div
              v-for="cat in historicoCategoriasFiltradas"
              :key="cat.seccion"
              class="cat-row"
            >
              <div class="cat-top">
                <span class="cat-name">{{ cat.seccion }}</span>
                <span
                  class="cat-val"
                  :style="{ color: getColor(cat.semaforo) }"
                >
                  {{ cat.score || "—" }}%
                </span>
              </div>
              <div class="bar">
                <span
                  class="bar-fill"
                  :style="{
                    width: barWidth(cat.score),
                    background: barGradient(cat.semaforo),
                  }"
                ></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Insight -->
        <div class="card-custom insight" style="margin-top: 16px">
          <div class="ic">💡</div>
          <div>
            <h4>Insight automático</h4>
            <p v-html="historicoInsightText"></p>
          </div>
        </div>

        <!-- Contacto -->
        <div
          v-if="historicoContacto"
          class="card-custom"
          style="margin-top: 16px"
        >
          <div class="sec-title">
            <span class="bar-accent contact-accent"></span>Contacto de la cuenta
          </div>
          <div class="sec-sub">Información del contacto asociado</div>
          <div style="margin-top: 10px">
            <div class="contact-info-grid">
              <div>
                <span class="contact-label">👤 Nombre:</span>
                <strong>{{ historicoContacto.nombre || "—" }}</strong>
              </div>
              <div>
                <span class="contact-label">💼 Puesto:</span>
                <strong>{{ historicoContacto.puesto || "—" }}</strong>
              </div>
              <div>
                <span class="contact-label">🏢 Cuenta:</span>
                <strong>{{ historicoContacto.cuenta || "—" }}</strong>
              </div>
              <div>
                <span class="contact-label">✉️ Email:</span>
                <strong>{{ historicoContacto.email || "—" }}</strong>
              </div>
              <div style="grid-column: 1/-1">
                <span class="contact-label">📞 Teléfono:</span>
                <strong>{{ historicoContacto.telefono || "—" }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Respuestas -->
        <div class="card-custom" style="margin-top: 16px">
          <div class="sec-title">
            <span class="bar-accent"></span>Respuestas capturadas
          </div>
          <div class="sec-sub">
            Detalle por sección y reactivo · valor histórico capturado
          </div>
          <div style="margin-top: 10px">
            <div
              v-for="(group, seccion) in historicoRespuestasAgrupadas"
              :key="seccion"
              class="resp-group"
            >
              <div class="resp-head">
                <span>📋 {{ seccion }}</span>
              </div>
              <div v-for="(r, idx) in group" :key="idx" class="resp-row">
                <div class="resp-q">
                  <span class="qn">{{ r.numPregunta || "" }}</span>
                  {{ r.texto || "" }}
                  <span v-if="r.tipo" class="tipo">{{ r.tipo }}</span>
                </div>
                <div class="resp-opt">{{ r.opcion || "—" }}</div>
                <div class="resp-val">{{ r.valorCapturado || "—" }}</div>
                <div v-if="r.comentario" class="resp-com">
                  "{{ r.comentario }}"
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Comentarios del cliente -->
        <div
          v-if="historicoComentariosCliente.length"
          class="card-custom"
          style="margin-top: 16px"
        >
          <div class="sec-title">
            <span class="bar-accent comments-accent"></span>
            💬 Comentarios del cliente por sección
          </div>
          <div class="sec-sub">
            Comentarios cualitativos capturados en cada sección
          </div>
          <div style="margin-top: 10px">
            <div
              v-for="(group, seccion) in historicoComentariosAgrupados"
              :key="seccion"
              class="comment-group"
            >
              <div class="comment-group-header">
                <span class="comment-group-title">📝 {{ seccion }}</span>
                <span class="comment-group-count"
                  >{{ group.length }} comentario{{
                    group.length > 1 ? "s" : ""
                  }}</span
                >
              </div>
              <div
                v-for="(com, idx) in group"
                :key="idx"
                class="comment-item"
                :class="{ 'comment-item-last': idx === group.length - 1 }"
              >
                <div class="comment-item-number">Comentario {{ idx + 1 }}</div>
                <div class="comment-item-text">"{{ com }}"</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Feedback interno -->
        <div
          v-if="historicoFeedback.length"
          class="card-custom"
          style="margin-top: 16px"
        >
          <div class="sec-title">
            <span class="bar-accent feedback-accent"></span>
            🏷️ Feedback interno del equipo
          </div>
          <div class="sec-sub">
            Comentarios internos sobre el desempeño por sección
          </div>
          <div style="margin-top: 10px">
            <div
              v-for="item in historicoFeedback"
              :key="item.seccion"
              class="feedback-item"
            >
              <div class="feedback-item-header">
                📋 {{ item.seccion || "Sin sección" }}
              </div>
              <div class="feedback-item-body">
                {{ item.feedback || "—" }}
              </div>
              <div class="feedback-item-date">
                {{ item.fecha || "" }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ================= VISTA DIRECTORIO CX ================= -->
    <section v-if="currentTab === 'dir'">
      <div class="filters" style="margin-top: 4px">
        <div style="max-width: 340px; flex: 1">
          <label for="dirCuenta">Cuenta</label>
          <select
            id="dirCuenta"
            v-model="dirFiltroCuenta"
            @change="aplicarFiltrosDirectorio"
          >
            <option value="">Todas</option>
            <option v-for="c in dirCuentasUnicas" :key="c" :value="c">
              {{ c }}
            </option>
          </select>
        </div>
        <div style="flex: 0 0 auto; display: flex; align-items: flex-end">
          <button
            class="btn-custom"
            :disabled="!dirContactosFiltrados.length"
            @click="descargarExcelDirectorio"
          >
            ⬇️ Descargar Excel
            <span style="font-weight: 500; opacity: 0.85">
              ({{ dirFiltroCuenta || "Todos" }})
            </span>
          </button>
        </div>
      </div>

      <div class="dir-kpis">
        <div
          v-for="stat in dirStats"
          :key="stat.modo"
          class="stat"
          :class="{
            warn: stat.warn,
            clickable: stat.modo !== 'total',
            active: dirModoActivo === stat.modo,
          }"
          @click="stat.modo !== 'total' && toggleDirModo(stat.modo)"
        >
          <div class="n">{{ stat.count }}</div>
          <div class="l">{{ stat.label }}</div>
        </div>
      </div>

      <div class="contact-grid">
        <div
          v-for="c in dirContactosFiltrados"
          :key="c.idContacto || c.idcontacto"
          class="contact-card"
          :class="{ warn: c.warnings }"
        >
          <div class="cc-name">{{ c.nombre || "—" }}</div>
          <div class="cc-puesto">{{ c.puesto || "—" }}</div>
          <div class="cc-cuenta">{{ c.cuenta || "" }}</div>
          <div v-if="c.email" class="cc-row">
            <span class="k">Email</span><span>{{ c.email }}</span>
          </div>
          <div v-if="c.telefono" class="cc-row">
            <span class="k">Teléfono</span><span>{{ c.telefono }}</span>
          </div>
          <div class="cc-tags">
            <span v-if="c.nivelInfluencia" class="tag-inf">{{
              c.nivelInfluencia
            }}</span>
            <span
              v-if="c.evaluacionCualitativa"
              class="eval-badge"
              :style="{
                color: getEvalColor(c.evaluacionCualitativa),
                borderColor: getEvalColor(c.evaluacionCualitativa) + '55',
              }"
              >{{ c.evaluacionCualitativa }}</span
            >
          </div>
          <div v-if="c.warnings" class="cc-warn">
            ⚠️ <span>{{ c.warnings }}</span>
          </div>
        </div>
      </div>

      <div v-if="!dirContactosFiltrados.length" class="empty">
        No hay contactos activos.
      </div>
    </section>

    <!-- ================= MODAL EDITAR EJECUCIÓN ================= -->
    <div v-if="editModalOpen" class="modal-overlay" @click.self="cerrarEditar">
      <div class="modal-box">
        <div class="modal-head">
          <h3>Editar ejecución #{{ editEjecucionId }}</h3>
          <button class="icon-btn" @click="cerrarEditar">✕</button>
        </div>

        <div class="modal-body">
          <div class="mode-switch">
            <button
              class="mode-btn"
              :class="{ active: editMode === 'cabecera' }"
              @click="editMode = 'cabecera'"
            >
              Solo cabecera (scores)
            </button>
            <button
              class="mode-btn"
              :class="{ active: editMode === 'respuestas' }"
              @click="editMode = 'respuestas'"
            >
              Respuestas completas
            </button>
          </div>

          <div v-if="loadingEdit" class="empty">
            <span class="spinner-sm dark"></span> Cargando datos actuales…
          </div>

          <template v-else>
            <!-- Modo cabecera -->
            <div v-if="editMode === 'cabecera'" class="row" style="margin-top: 12px">
              <div style="max-width: 120px">
                <label>NPS (0–10)</label>
                <input type="number" min="0" max="10" v-model.number="editForm.nps" />
              </div>
              <div style="max-width: 120px">
                <label>CES (0–10)</label>
                <input type="number" min="0" max="10" v-model.number="editForm.ces" />
              </div>
              <div style="max-width: 120px">
                <label>CSAT (0–10)</label>
                <input type="number" min="0" max="10" v-model.number="editForm.csat" />
              </div>
            </div>

            <!-- Modo respuestas -->
            <div v-else>
              <div class="muted" style="font-size: 12px; margin: 8px 0">
                Al guardar se reemplazan las respuestas y el backend recalcula el
                ponderado.
              </div>
              <div style="max-width: 140px; margin-bottom: 12px">
                <label>NPS (0–10)</label>
                <input type="number" min="0" max="10" v-model.number="editForm.nps" />
              </div>
              <div
                v-for="seccion in encuestaSecciones"
                :key="'edit-' + (seccion.id || seccion.seccion)"
                class="sec"
                style="margin-bottom: 10px"
              >
                <h3 style="font-size: 15px">{{ seccion.seccion }}</h3>
                <div
                  v-for="pregunta in seccion.preguntas"
                  :key="'editq-' + pregunta.idPregunta"
                  class="q"
                >
                  <p>{{ pregunta.numPregunta }}. {{ pregunta.texto }}</p>
                  <div v-if="pregunta.tipo === 'ABIERTA'" class="opts">
                    <textarea
                      rows="2"
                      v-model="editRespuestas[pregunta.idPregunta]"
                    ></textarea>
                  </div>
                  <div v-else class="opts">
                    <label
                      v-for="opcion in pregunta.opciones"
                      :key="'edito-' + opcion.idOpcion"
                      class="radio-label"
                    >
                      <input
                        type="radio"
                        :name="'edit-p' + pregunta.idPregunta"
                        :value="opcion.idOpcion"
                        v-model="editRespuestas[pregunta.idPregunta]"
                      />
                      {{ opcion.etiqueta }}
                      <span class="muted">({{ opcion.valor }})</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-foot">
          <button class="btn-custom secondary" @click="cerrarEditar">
            Cancelar
          </button>
          <button
            class="btn-custom"
            :disabled="savingEdit || loadingEdit"
            @click="guardarEdicion"
          >
            <span v-if="savingEdit" class="spinner-sm"></span>
            {{ savingEdit ? "Guardando…" : "Guardar cambios" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ================= MODAL ELIMINAR EJECUCIÓN ================= -->
    <div
      v-if="deleteModalOpen"
      class="modal-overlay"
      @click.self="cerrarEliminar"
    >
      <div class="modal-box" style="max-width: 460px">
        <div class="modal-head">
          <h3>Eliminar ejecución</h3>
          <button class="icon-btn" @click="cerrarEliminar">✕</button>
        </div>
        <div class="modal-body">
          <p class="muted" style="margin-bottom: 12px">
            ¿Cómo deseas eliminar la ejecución
            <b>#{{ deleteEjecucion && (deleteEjecucion.idEjecucion || deleteEjecucion.idejecucion) }}</b>
            de
            <b>{{ deleteEjecucion && deleteEjecucion.cliente }}</b
            >?
          </p>
          <label class="radio-stack">
            <input type="radio" value="logico" v-model="deleteTipo" />
            <span>
              <b>Borrado lógico</b> (recomendado) — marca la ejecución como
              inactiva, conservando el histórico.
            </span>
          </label>
          <label class="radio-stack">
            <input type="radio" value="fisico" v-model="deleteTipo" />
            <span>
              <b>Borrado físico</b> — elimina definitivamente respuestas y
              cabecera. No se puede deshacer.
            </span>
          </label>
        </div>
        <div class="modal-foot">
          <button class="btn-custom secondary" @click="cerrarEliminar">
            Cancelar
          </button>
          <button
            class="btn-custom danger"
            :disabled="deletingExec"
            @click="confirmarEliminar"
          >
            <span v-if="deletingExec" class="spinner-sm"></span>
            {{ deletingExec ? "Eliminando…" : "Eliminar" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ================= MODAL ANÁLISIS EJECUTIVO ================= -->
    <div
      v-if="analysisModalOpen"
      class="modal-overlay"
      @click.self="cerrarAnalisis"
    >
      <div class="modal-box" style="max-width: 820px">
        <div class="modal-head">
          <h3>🧠 Análisis Ejecutivo CX</h3>
          <button class="icon-btn" @click="cerrarAnalisis">✕</button>
        </div>
        <div class="modal-body">
          <div v-if="analysisLoading" class="ai-progress" aria-live="polite">
            <div class="ai-progress-orb" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path
                  d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z"
                />
                <path
                  d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z"
                />
              </svg>
            </div>
            <h4 class="ai-progress-title">
              Generando el análisis ejecutivo{{
                analysisCliente ? ` de ${analysisCliente}` : ""
              }}
            </h4>
            <p class="ai-progress-desc">
              La IA revisa las calificaciones por categoría, los indicadores
              NPS, CES y CSAT, los comentarios de cada sección y el feedback
              interno del equipo para redactar un informe listo para compartir.
            </p>

            <ol class="ai-steps">
              <li
                v-for="(paso, i) in analysisPasos"
                :key="paso"
                :class="{
                  done: i < analysisStep,
                  current: i === analysisStep,
                }"
              >
                <span class="ai-step-dot" aria-hidden="true"></span>
                {{ paso }}
              </li>
            </ol>

            <div class="ai-progress-includes">
              <span class="ai-includes-label">El informe incluirá</span>
              <div class="ai-chips">
                <span>Resumen ejecutivo</span>
                <span>Diagnóstico por categoría</span>
                <span>Riesgos</span>
                <span>Recomendaciones</span>
                <span>Quick wins</span>
              </div>
            </div>
            <p class="ai-progress-note">
              Puede tardar unos segundos. Puedes cerrar esta ventana; el
              resultado no se guarda, tendrías que generarlo de nuevo.
            </p>
          </div>
          <div v-else-if="analysisError" class="analysis-error">
            ⚠️ {{ analysisError }}
          </div>
          <div
            v-else
            class="markdown-body"
            v-html="analysisHtml"
          ></div>
        </div>
        <div class="modal-foot">
          <button
            v-if="analysisResult && !analysisLoading"
            class="btn-custom secondary"
            @click="copiarAnalisis"
          >
            📋 Copiar
          </button>
          <button class="btn-custom" @click="cerrarAnalisis">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- TOAST -->
    <div class="toast-custom" :class="{ show: toastMessage }">
      {{ toastMessage }}
    </div>
  </main>
</template>

<script>
import { apiGet, apiPost, apiPut, apiDelete } from "@/services/api"; // Ajusta la ruta según tu proyecto
import { jsPDF } from "jspdf";
import { analizarEncuesta } from "@/services/analysis.service";
import { track } from "@/services/telemetry";
import { renderMarkdown } from "@/utils/markdown";
export default {
  name: "DashboardIndex",

  data() {
    return {
      // Estado general
      currentTab: "actual",
      toastMessage: "",
      toastTimeout: null,

      // Rol del usuario autenticado (admin | editor)
      userRole: (localStorage.getItem("rol") || "").toLowerCase(),

      // Cuentas
      cuentas: [],
      cuentaSeleccionada: null,
      cuentaSel: null,

      // Dashboard
      dashboardData: null,
      categorias: [],
      feedbackItems: [],
      contactoDashboard: null,

      // Encuesta
      encuestaData: null,
      encuestaSecciones: [],
      respuestas: {},
      comentariosSeccion: {}, // keyed por NOMBRE de sección (soporta secciones dinámicas)

      // Captura: paso previo (selección de secciones + período)
      capturaStep: "secciones", // 'secciones' | 'form'
      seccionesDisponibles: [
        "COMERCIAL",
        "DESEMPEÑO OPERATIVO",
        "INSTALACIONES",
        "KPI CX",
        "SISTEMAS",
        "TRANSPORTE",
      ],
      seccionesSeleccionadas: [
        "COMERCIAL",
        "DESEMPEÑO OPERATIVO",
        "INSTALACIONES",
        "KPI CX",
        "SISTEMAS",
        "TRANSPORTE",
      ],
      capturaMes: new Date().getMonth() + 1,
      capturaAnio: new Date().getFullYear(),
      mesesNombres: [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
      ],
      loadingGuardar: false,

      // Histórico: edición / eliminación de ejecuciones
      loadingHist: false,
      editModalOpen: false,
      editMode: "cabecera", // 'cabecera' | 'respuestas'
      editEjecucionId: null,
      editForm: { nps: 0, ces: 0, csat: 0 },
      editRespuestas: {}, // idPregunta -> idOpcion | comentario
      loadingEdit: false,
      savingEdit: false,
      deleteModalOpen: false,
      deleteEjecucion: null,
      deleteTipo: "logico", // 'logico' | 'fisico'
      deletingExec: false,

      // Análisis Ejecutivo (Mistral)
      analysisModalOpen: false,
      analysisLoading: false,
      analysisResult: "",
      analysisError: "",
      analysisCliente: "",
      // Pasos ilustrativos que se muestran mientras responde el modelo
      analysisPasos: [
        "Recopilando respuestas y comentarios",
        "Evaluando indicadores y categorías",
        "Identificando riesgos y oportunidades",
        "Redactando recomendaciones",
      ],
      analysisStep: 0,
      analysisTimer: null,

      // KPIs
      kpiSelectors: [
        { id: "nps", label: "NPS", value: 9 },
        { id: "ces", label: "CES", value: 8 },
        { id: "csat", label: "CSAT", value: 9 },
      ],

      // Contactos
      contactosAll: [],
      contactoSeleccionadoId: "",
      contactoSeleccionadoData: null,

      // Feedback
      feedbackEjecucionId: null,
      feedbackData: {},

      // Histórico
      histLevel: "filtros",
      histFiltroCuenta: "",
      histFiltroEncuesta: "",
      histFiltroFecha: "",
      historicoEjecuciones: [],
      historicoDetalle: null,
      historicoContacto: null,
      historicoFeedback: [],
      historicoComentariosCliente: [],

      // Directorio
      dirFiltroCuenta: "",
      dirModoActivo: null,
      dirContactosFiltrados: [],

      // Constantes
      circleCircumference: 2 * Math.PI * 42,
    };
  },

  computed: {
    // Cliente nombre
    clienteNombre() {
      if (this.cuentaSel) return this.cuentaSel.nombre || "";
      return "";
    },

    // Encuesta nombre
    encuestaNombre() {
      if (this.encuestaData) {
        return `${this.encuestaData.nombre || ""} · ${
          this.encuestaData.clave || ""
        } v${this.encuestaData.version || ""}`;
      }
      return "";
    },

    // Score formateado para el dashboard actual
    formattedScore() {
      return Math.round(this.getScorePct(this.dashboardData));
    },

    // Score formateado para el histórico
    formattedHistScore() {
      return Math.round(this.getScorePct(this.historicoDetalle));
    },

    // Contactos filtrados por cuenta
    contactosFiltrados() {
      if (!this.cuentaSel || !this.contactosAll.length) return [];
      const idCuenta = this.cuentaSel.idCuenta || this.cuentaSel.idcuenta;
      return this.contactosAll.filter((c) => {
        const cid = c.idCuenta || c.idcuenta;
        return String(cid) === String(idCuenta);
      });
    },

    // Categorías filtradas (excluyendo KPIS)
    categoriasFiltradas() {
      const exclude = ["KPI", "KPIS"];
      const filtradas = (this.categorias || []).filter((c) => {
        const nombre = (c.seccion || "").toUpperCase().trim();
        return !exclude.some((k) => nombre.includes(k));
      });
      return this.ordenarSecciones(filtradas);
    },

    // KPI List para dashboard
    kpiList() {
      return this.buildKpiList(this.dashboardData);
    },

    // Gauge SVG
    gaugeSVG() {
      return this.buildGauge(this.getScorePct(this.dashboardData));
    },

    // Status chip style
    statusChipStyle() {
      const col = this.getColor(this.dashboardData?.semaforo);
      return {
        color: col,
        background: this.hexaColor(col, 0.12),
        borderColor: this.hexaColor(col, 0.3),
      };
    },

    // Insight text
    insightText() {
      return this.buildInsightText(this.categoriasFiltradas);
    },

    // Breadcrumb del histórico
    histBreadcrumbText() {
      const cuenta = this.histFiltroCuenta
        ? this.cuentas.find(
            (c) =>
              String(c.idCuenta || c.idcuenta) ===
              String(this.histFiltroCuenta),
          )?.nombre || "Cliente"
        : "Cliente";
      const enc = this.encuestaData?.nombre || "Encuesta";
      return `${cuenta} · ${enc}`;
    },

    historicoEncuestaMeta() {
      const d = this.historicoDetalle;
      if (!d) return "";
      const enc = d.encuesta || {};
      return `${enc.nombre || ""}${
        enc.clave ? " · " + enc.clave + " v" + enc.version : ""
      }${d.periodo ? " · " + d.periodo : ""}`;
    },

    historicoGaugeSVG() {
      return this.buildGauge(this.getScorePct(this.historicoDetalle));
    },

    historicoStatusChipStyle() {
      const col = this.getColor(this.historicoDetalle?.semaforo);
      return {
        color: col,
        background: this.hexaColor(col, 0.12),
        borderColor: this.hexaColor(col, 0.3),
      };
    },

    historicoKpiList() {
      return this.buildKpiList(this.historicoDetalle);
    },

    historicoCategoriasFiltradas() {
      const exclude = ["KPI", "KPIS"];
      const cats = this.historicoDetalle?.categorias || [];
      const filtradas = cats.filter((c) => {
        const nombre = (c.seccion || "").toUpperCase().trim();
        return !exclude.some((k) => nombre.includes(k));
      });
      return this.ordenarSecciones(filtradas);
    },

    historicoInsightText() {
      return this.buildInsightText(this.historicoCategoriasFiltradas);
    },

    historicoRespuestasAgrupadas() {
      const respuestas = this.historicoDetalle?.respuestas || [];
      const groups = {};
      respuestas.forEach((r) => {
        const sec = r.seccion || "Sin sección";
        if (!groups[sec]) groups[sec] = [];
        groups[sec].push(r);
      });
      return groups;
    },

    historicoComentariosAgrupados() {
      const comments = this.historicoComentariosCliente || [];
      const groups = {};
      comments.forEach((c) => {
        const sec = c.seccion || "Sin sección";
        const com = c.comentario || c.COMENTARIO || "";
        if (com.trim()) {
          if (!groups[sec]) groups[sec] = [];
          groups[sec].push(com);
        }
      });
      return groups;
    },

    // Directorio
    dirCuentasUnicas() {
      const cuentas = [
        ...new Set(
          this.contactosAll
            .map((c) => c.cuenta || c.CUENTA || "")
            .filter(Boolean),
        ),
      ];
      return cuentas.sort((a, b) => a.localeCompare(b));
    },

    dirStats() {
      const list = this.dirFiltroCuenta
        ? this.contactosAll.filter(
            (c) => (c.cuenta || c.CUENTA) === this.dirFiltroCuenta,
          )
        : this.contactosAll;

      const total = list.length;
      const dm = list.filter((c) => {
        const inf = (
          c.nivelInfluencia ||
          c.nivelinfluencia ||
          ""
        ).toUpperCase();
        return inf.includes("DECISION");
      }).length;

      return [
        { modo: "total", label: "Total contactos", count: total, warn: false },
        { modo: "decision", label: "Decision makers", count: dm, warn: false },
      ];
    },

    // Secciones de la encuesta que el usuario activó en el paso previo
    seccionesActivas() {
      const keys = this.seccionesSeleccionadas
        .map((l) => this.seccionKeyword(l))
        .filter(Boolean);
      return (this.encuestaSecciones || []).filter((sec) => {
        const n = this.normalizaSeccion(sec.seccion);
        return keys.some((k) => n.includes(k));
      });
    },

    // Años disponibles para el selector de período (ventana razonable)
    aniosCaptura() {
      const y = new Date().getFullYear();
      const arr = [];
      for (let i = y - 3; i <= y + 2; i++) arr.push(i);
      return arr;
    },

    // HTML renderizado del análisis ejecutivo (Markdown seguro)
    analysisHtml() {
      return renderMarkdown(this.analysisResult);
    },

    // Autorización por rol: admin edita y elimina; editor solo edita.
    esAdmin() {
      return (this.userRole || "").includes("admin");
    },
    puedeEditar() {
      const r = this.userRole || "";
      return r.includes("admin") || r.includes("editor");
    },
  },

  watch: {
    cuentaSeleccionada(newVal) {
      if (newVal) {
        this.cuentaSel = this.cuentas.find(
          (c) => String(c.idCuenta || c.idcuenta) === String(newVal),
        );
        this.cargarDashboard();
        this.actualizarContactos();
      } else {
        this.cuentaSel = null;
        this.dashboardData = null;
        this.categorias = [];
        this.feedbackItems = [];
        this.contactoDashboard = null;
      }
    },

    contactoSeleccionadoId(newVal) {
      if (newVal) {
        this.contactoSeleccionadoData =
          this.contactosAll.find(
            (c) => String(c.idContacto || c.idcontacto) === String(newVal),
          ) || null;
      } else {
        this.contactoSeleccionadoData = null;
      }
    },

    histLevel() {
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
  },

  mounted() {
    this.init();
  },

  beforeUnmount() {
    clearInterval(this.analysisTimer);
  },

  methods: {
    // ============ INICIALIZACIÓN ============
    async init() {
      try {
        // Cargar cuentas
        const data = await apiGet("/cuentas");
        this.cuentas = data.items || [];

        // Cargar encuesta
        this.encuestaData = await apiGet("/encuestas/1");
        if (this.encuestaData) {
          this.encuestaSecciones = this.ordenarSecciones(
            this.encuestaData.secciones || [],
          );
        }

        // Cargar contactos
        const contactData = await apiGet("/contactos");
        this.contactosAll = contactData.items || [];

        // Cargar dashboard si hay cuenta seleccionada
        if (this.cuentaSeleccionada) {
          await this.cargarDashboard();
        }
      } catch (error) {
        console.error("Error en init:", error);
        this.mostrarToast("Error cargando datos iniciales");
      }
    },

    // ============ TOAST ============
    mostrarToast(mensaje) {
      this.toastMessage = mensaje;
      clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => {
        this.toastMessage = "";
      }, 2800);
    },

    // ============ SECCIONES DINÁMICAS ============
    normalizaSeccion(s) {
      return (s || "")
        .toString()
        .toUpperCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .trim();
    },

    // Orden canónico de secciones en todas las vistas:
    // Desempeño Operativo, Transporte, Instalaciones, Comercial, Sistemas y KPI CX (último)
    ordenSeccionIndex(nombre) {
      const n = this.normalizaSeccion(nombre);
      const orden = [
        "DESEMPEN",
        "TRANSPORTE",
        "INSTALACION",
        "COMERCIAL",
        "SISTEMA",
        "KPI",
      ];
      const i = orden.findIndex((k) => n.includes(k));
      return i === -1 ? orden.length : i;
    },

    // Devuelve una copia ordenada de secciones/categorías (por su campo .seccion)
    ordenarSecciones(items) {
      return (items || [])
        .slice()
        .sort(
          (a, b) =>
            this.ordenSeccionIndex(a && a.seccion) -
            this.ordenSeccionIndex(b && b.seccion),
        );
    },

    // Palabra clave para emparejar la etiqueta del checkbox con el nombre real
    // de la sección que devuelve ORDS (que puede variar: "KPIS CX" vs "KPI CX").
    seccionKeyword(label) {
      const map = {
        COMERCIAL: "COMERCIAL",
        "DESEMPEÑO OPERATIVO": "DESEMPENO",
        INSTALACIONES: "INSTALACION",
        "KPI CX": "KPI",
        SISTEMAS: "SISTEMA",
        TRANSPORTE: "TRANSPORTE",
      };
      return map[label];
    },

    toggleSeccion(label) {
      const i = this.seccionesSeleccionadas.indexOf(label);
      if (i >= 0) this.seccionesSeleccionadas.splice(i, 1);
      else this.seccionesSeleccionadas.push(label);
    },

    // ¿La sección corresponde a KPIS CX? (aloja los indicadores 0-10)
    esSeccionKpi(seccion) {
      return this.normalizaSeccion(seccion && seccion.seccion).includes("KPI");
    },

    // Color por intervalo para NPS/CES/CSAT: rojo 0-6, naranja 7-8, verde 9-10
    kpiColor(val) {
      const v = Number(val);
      if (v <= 6) return "#FF5D73"; // rojo
      if (v === 7 || v === 8) return "#FFC24B"; // naranja
      return "#43B234"; // verde (9-10)
    },

    // Estilo de cada botón 0-10 según su valor y si está seleccionado
    kpiBtnStyle(val, selected) {
      const c = this.kpiColor(val);
      if (selected) {
        return {
          background: c,
          borderColor: c,
          color: "#fff",
          boxShadow: `0 2px 8px ${this.hexaColor(c, 0.35)}`,
        };
      }
      return {
        background: this.hexaColor(c, 0.1),
        borderColor: this.hexaColor(c, 0.3),
        color: c,
      };
    },

    // Paso previo → formulario de captura
    continuarCaptura() {
      if (!this.seccionesSeleccionadas.length) {
        this.mostrarToast("Selecciona al menos una sección");
        return;
      }
      const mes = Number(this.capturaMes);
      const anio = Number(this.capturaAnio);
      if (!mes || mes < 1 || mes > 12 || !anio) {
        this.mostrarToast("Indica un período válido (mes y año)");
        return;
      }
      if (!this.seccionesActivas.length) {
        this.mostrarToast(
          "Las secciones seleccionadas no existen en la encuesta vigente",
        );
        return;
      }
      this.capturaStep = "form";
      window.scrollTo(0, 0);
    },

    // ============ UTILIDADES ============
    getCssVar(name) {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
    },

    hexaColor(color, alpha) {
      if (!color || color[0] !== "#") return color;
      const n = parseInt(color.slice(1), 16);
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${
        n & 255
      }, ${alpha})`;
    },

    getColor(semaforo) {
      if (!semaforo) return "#D4DFF0";
      const s = semaforo.toUpperCase();
      if (s === "VERDE" || s === "GREEN") return "#43B234";
      if (s === "AMARILLO" || s === "YELLOW") return "#FFC24B";
      if (s === "ROJO" || s === "RED") return "#FF5D73";
      return "#D4DFF0";
    },

    getColorByScore(score) {
      if (score == null) return "#6c757d";
      if (score >= 80) return "#28a745";
      if (score >= 60) return "#ffc107";
      return "#dc3545";
    },

    getEvalColor(evalValue) {
      if (!evalValue) return "var(--txt-dim)";
      const e = evalValue.toUpperCase();
      if (e === "PROMOTOR") return "var(--good)";
      if (e === "NEUTRO") return "var(--warn)";
      if (e === "DETRACTOR") return "var(--bad)";
      return "var(--txt-dim)";
    },

    barWidth(score) {
      return Math.min(100, Math.max(0, Number(score) || 0)) + "%";
    },

    barGradient(semaforo) {
      const color = this.getColor(semaforo);
      return `linear-gradient(90deg, ${color}, ${color}cc)`;
    },

    // Construir lista de KPIs
    buildKpiList(data) {
      if (!data) return [];
      const kpis = data.kpis || {};
      const nps = Number(kpis.nps ?? data.nps);
      const ces = Number(kpis.ces ?? data.ces);
      const csat = Number(kpis.csat ?? data.csat);
      const values = [nps, ces, csat].filter((v) => !isNaN(v));
      const promedio = values.length
        ? values.reduce((a, b) => a + b, 0) / values.length
        : null;

      const items = [
        { name: "NPS", value: nps, desc: "Probabilidad de recomendar" },
        { name: "CES", value: ces, desc: "Facilidad para ser atendido" },
        { name: "CSAT", value: csat, desc: "Satisfacción con el servicio" },
        {
          name: "PROMEDIO CX",
          value: promedio,
          desc: "Promedio de indicadores CX",
        },
      ];

      return items.map((item) => {
        const val =
          item.value !== null && !isNaN(item.value)
            ? Math.max(0, Math.min(100, Number(item.value) * 10))
            : 0;
        const color =
          val >= 90
            ? this.getCssVar("--good")
            : val >= 70
            ? this.getCssVar("--lime")
            : val >= 50
            ? this.getCssVar("--warn")
            : this.getCssVar("--bad");
        return {
          ...item,
          displayValue:
            item.value !== null && !isNaN(item.value)
              ? Math.round(item.value * 10)
              : "—",
          color,
          offset: this.circleCircumference * (1 - val / 100),
        };
      });
    },

    // Construir texto de insight
    buildInsightText(categorias) {
      const cats = categorias
        .map((c) => ({ n: c.seccion, s: Number(c.score) }))
        .filter((c) => c.n && !isNaN(c.s));

      if (!cats.length)
        return "Aún no hay categorías evaluadas para generar un insight.";
      if (cats.length === 1)
        return `Única categoría evaluada: <b>${cats[0].n}</b> (${cats[0].s}%).`;

      const max = cats.reduce((a, b) => (b.s > a.s ? b : a));
      const min = cats.reduce((a, b) => (b.s < a.s ? b : a));
      return (
        `Categoría más fuerte: <b style="color:var(--good)">${max.n}</b> (${max.s}%). ` +
        `Categoría más débil: <b style="color:var(--bad)">${min.n}</b> (${min.s}%).`
      );
    },

    // Extrae el score (0-100) de una ejecución/dashboard. Algunas respuestas
    // del backend llegan doblemente escaladas (100% → 10000), así que si el
    // valor excede 100 se reescala dividiendo entre 100.
    getScorePct(d) {
      if (!d) return 0;
      const raw =
        d.scorePct ??
        d.scorepct ??
        d.ScorePct ??
        d.SCOREPCT ??
        d.score ??
        d.puntuacion ??
        d.calificacion ??
        d.calificacionGlobal ??
        0;
      let n = Number(raw);
      if (isNaN(n)) return 0;
      if (n > 100) n = n / 100;
      return Math.min(100, n);
    },

    // Construir gauge SVG (CORREGIDO)
    buildGauge(score) {
      const cx = 180,
        cy = 180,
        r = 140,
        rin = 104;

      // Asegurar que score sea un número válido
      let safeScore = Number(score);
      if (isNaN(safeScore) || safeScore === undefined || safeScore === null) {
        safeScore = 0;
      }
      const clampedScore = Math.max(0, Math.min(100, safeScore));

      const polar = (d) => {
        const a = (d * Math.PI) / 180;
        return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
      };
      const polarIn = (d) => {
        const a = (d * Math.PI) / 180;
        return [cx + rin * Math.cos(a), cy - rin * Math.sin(a)];
      };

      const segColors = [
        "#FF5D73", // Rojo (0-20%)
        "#FF8A4B", // Naranja (20-40%)
        "#FFC24B", // Amarillo (40-60%)
        "#5CC94E", // Lima (60-80%)
        "#43B234", // Verde (80-100%)
      ];

      // Los segmentos van de 180° (izquierda) a 0° (derecha)
      let segs = "";
      for (let i = 0; i < 5; i++) {
        const a0 = 180 - i * 36; // 180, 144, 108, 72, 36
        const a1 = 180 - (i + 1) * 36; // 144, 108, 72, 36, 0
        const [x0, y0] = polar(a0),
          [x1, y1] = polar(a1);
        const [ix1, iy1] = polarIn(a1),
          [ix0, iy0] = polarIn(a0);
        segs += `<path d="M${x0} ${y0} A${r} ${r} 0 0 1 ${x1} ${y1} L${ix1} ${iy1} A${rin} ${rin} 0 0 0 ${ix0} ${iy0} Z" fill="${segColors[i]}" opacity=".85"/>`;
      }

      // Ticks de 0% a 100%
      let ticks = "";
      for (let v = 0; v <= 100; v += 10) {
        const angle = 180 - (v / 100) * 180; // 180° (0%) a 0° (100%)
        const a = (angle * Math.PI) / 180;
        const xo = cx + (r + 8) * Math.cos(a),
          yo = cy - (r + 8) * Math.sin(a);
        const xi = cx + (r - 4) * Math.cos(a),
          yi = cy - (r - 4) * Math.sin(a);
        ticks += `<line x1="${xi}" y1="${yi}" x2="${xo}" y2="${yo}" stroke="rgba(255,255,255,.15)" stroke-width="2"/>`;
      }

      // CORRECCIÓN: La aguja debe rotar de 180° (0%) a 0° (100%)
      // 0% = 180° | 50% = 90° | 100% = 0°
      const needleAngle = -(180 - (clampedScore / 100) * 180);

      console.log("Score:", clampedScore, "→ Ángulo aguja:", needleAngle); // Debug

      return `
    <svg viewBox="0 0 360 210" style="width:100%;overflow:visible">
      <defs>
        <filter id="ndsh" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="#000" flood-opacity=".4"/>
        </filter>
      </defs>
      ${segs}
      ${ticks}
      <g style="transform:rotate(${needleAngle}deg);transform-origin:${cx}px ${cy}px;transition:transform 1.8s cubic-bezier(.18,1,.3,1)">
        <polygon points="${cx - 6},${cy} ${cx + r - 22},${cy - 4} ${
        cx + r - 18
      },${cy} ${cx + r - 22},${cy + 4} ${cx - 6},${
        cy + 8
      }" fill="#ffffff" filter="url(#ndsh)"/>
      </g>
      <circle cx="${cx}" cy="${cy}" r="13" fill="#1a2a4a" stroke="#ffffff" stroke-width="3"/>
      <circle cx="${cx}" cy="${cy}" r="4" fill="#43B234"/>
    </svg>
  `;
    },

    // ============ TABS ============
    showTab(tab) {
      this.currentTab = tab;
      if (tab === "hist") {
        this.histLevel = "filtros";
      }
      if (tab === "dir") {
        this.aplicarFiltrosDirectorio();
      }
      window.scrollTo(0, 0);
    },

    // ============ DASHBOARD ============
    onSelectCuenta() {
      if (this.cuentaSeleccionada) {
        this.cuentaSel = this.cuentas.find(
          (c) =>
            String(c.idCuenta || c.idcuenta) ===
            String(this.cuentaSeleccionada),
        );
        this.cargarDashboard();
        this.actualizarContactos();
      } else {
        this.cuentaSel = null;
        this.dashboardData = null;
        this.categorias = [];
        this.feedbackItems = [];
        this.contactoDashboard = null;
      }
    },

    async cargarDashboard() {
      if (!this.cuentaSel) return;
      const idCuenta = this.cuentaSel.idCuenta || this.cuentaSel.idcuenta;

      try {
        const k = await apiGet(`/dashboard/cuenta/${idCuenta}`);
        console.log(
          "Dashboard API response completa:",
          JSON.stringify(k, null, 2),
        ); // DEBUG

        const kpi = (k.items || [])[0];
        console.log("KPI data completa:", JSON.stringify(kpi, null, 2)); // DEBUG

        // Listar todas las propiedades del objeto kpi
        if (kpi) {
          console.log("Propiedades disponibles en KPI:", Object.keys(kpi)); // DEBUG
          for (const [key, value] of Object.entries(kpi)) {
            console.log(`  ${key}: ${value} (tipo: ${typeof value})`); // DEBUG
          }
        }

        if (!kpi || (kpi.tieneDatos || kpi.tienedatos) === "N") {
          this.dashboardData = null;
          this.categorias = [];
          this.feedbackItems = [];
          return;
        }

        this.dashboardData = kpi;

        // Cargar categorías
        try {
          const cat = await apiGet(`/dashboard/cuenta/${idCuenta}/categorias`);
          this.categorias = cat.items || [];
        } catch (e) {
          this.categorias = [];
        }

        // Cargar feedback
        const idEjecucion = kpi.idEjecucion || kpi.idejecucion;
        if (idEjecucion) {
          try {
            const fb = await apiGet(`/ejecuciones/${idEjecucion}/feedback`);
            this.feedbackItems = fb.feedback || [];
          } catch (e) {
            this.feedbackItems = [];
          }
        } else {
          this.feedbackItems = [];
        }

        // Cargar contacto
        this.contactoDashboard = this.getContactoPrincipalByCuenta(idCuenta);
      } catch (error) {
        console.error("Error cargando dashboard:", error);
        this.mostrarToast("Error cargando dashboard");
        this.dashboardData = null;
        this.categorias = [];
        this.feedbackItems = [];
      }
    },

    // ============ CONTACTOS ============
    actualizarContactos() {
      if (!this.cuentaSel) return;
      const idCuenta = this.cuentaSel.idCuenta || this.cuentaSel.idcuenta;
      this.contactoDashboard = this.getContactoPrincipalByCuenta(idCuenta);
      this.contactoSeleccionadoId = "";
      this.contactoSeleccionadoData = null;
    },

    getContactosByCuenta(idCuenta) {
      if (!idCuenta || !this.contactosAll.length) return [];
      return this.contactosAll.filter((c) => {
        const cid = c.idCuenta || c.idcuenta;
        return String(cid) === String(idCuenta);
      });
    },

    getContactoPrincipalByCuenta(idCuenta) {
      const contactos = this.getContactosByCuenta(idCuenta);
      return contactos.length ? contactos[0] : null;
    },

    // Contacto que respondió una ejecución, a partir del idContacto que trae su
    // detalle. Se resuelve contra el directorio para completar email y cuenta,
    // que el detalle no incluye; si no está ahí, se arma con lo que sí trae.
    contactoDeEjecucion(d) {
      if (!d) return null;
      const idContacto = d.idContacto || d.idcontacto;
      if (!idContacto) return null;

      const delDirectorio = this.contactosAll.find(
        (c) => String(c.idContacto || c.idcontacto) === String(idContacto),
      );
      if (delDirectorio) return delDirectorio;

      return {
        nombre: d.nombreContacto || d.nombrecontacto || null,
        puesto: d.puestoContacto || d.puestocontacto || null,
        cuenta: d.cliente || null,
        email: null,
        telefono: d.telefonoContacto || d.telefonocontacto || null,
      };
    },

    onContactoChange() {
      // El watch ya maneja la actualización
    },

    // ============ CAPTURA ============
    openCapture() {
      if (!this.cuentaSeleccionada) {
        this.mostrarToast("Selecciona un cliente primero");
        return;
      }
      this.currentTab = "captura";
      this.capturaStep = "secciones"; // primero el paso de secciones + período
      this.respuestas = {};
      this.comentariosSeccion = {};
      this.contactoSeleccionadoId = "";
      this.contactoSeleccionadoData = null;

      // Reset del paso previo
      this.seccionesSeleccionadas = [...this.seccionesDisponibles];
      this.capturaMes = new Date().getMonth() + 1;
      this.capturaAnio = new Date().getFullYear();

      // Inicializar respuestas con valores por defecto
      if (this.encuestaSecciones) {
        this.encuestaSecciones.forEach((sec) => {
          (sec.preguntas || []).forEach((p) => {
            if (p.tipo === "ABIERTA") {
              this.respuestas[p.idPregunta] = "";
            } else {
              this.respuestas[p.idPregunta] = null;
            }
          });
        });
      }

      // Reset KPIs a valores por defecto
      this.kpiSelectors.forEach((kpi) => {
        kpi.value = 9;
      });

      window.scrollTo(0, 0);
    },

    cancelarCaptura() {
      if (Object.values(this.respuestas).some((v) => v !== null && v !== "")) {
        if (!confirm("¿Estás seguro? Los datos capturados se perderán.")) {
          return;
        }
      }
      this.currentTab = "actual";
      this.cargarDashboard();
    },

    async guardarEncuesta() {
      if (!this.cuentaSel) {
        this.mostrarToast("Selecciona un cliente primero");
        return;
      }

      // Período obligatorio
      const mes = Number(this.capturaMes);
      const anio = Number(this.capturaAnio);
      if (!mes || mes < 1 || mes > 12 || !anio) {
        this.mostrarToast("Indica un período válido (mes y año)");
        return;
      }

      // Validar respuestas SOLO de las secciones activas
      let respuestasArray = [];
      let faltan = 0;

      for (const sec of this.seccionesActivas) {
        for (const p of sec.preguntas || []) {
          if (p.tipo === "ABIERTA") {
            const val = this.respuestas[p.idPregunta] || "";
            if (val.trim()) {
              respuestasArray.push({
                idPregunta: p.idPregunta,
                comentario: val.trim(),
              });
            }
          } else {
            const val = this.respuestas[p.idPregunta];
            if (val != null) {
              respuestasArray.push({
                idPregunta: p.idPregunta,
                idOpcion: Number(val),
              });
            } else if (p.obligatorio !== false) {
              faltan++;
            }
          }
        }
      }

      if (faltan > 0) {
        this.mostrarToast(
          `Faltan ${faltan} pregunta(s) obligatoria(s) por responder`,
        );
        return;
      }

      // Comentarios por sección (clave = NOMBRE de sección), solo activas
      const activas = new Set(this.seccionesActivas.map((s) => s.seccion));
      const comentariosSeccionArray = [];
      for (const [seccion, comentario] of Object.entries(
        this.comentariosSeccion,
      )) {
        if (comentario && comentario.trim() && activas.has(seccion)) {
          comentariosSeccionArray.push({
            seccion,
            comentario: comentario.trim(),
          });
        }
      }

      const mm = String(mes).padStart(2, "0");
      const body = {
        idEncuesta: this.encuestaData.idEncuesta,
        idCuenta: this.cuentaSel.idCuenta || this.cuentaSel.idcuenta,
        // Período capturado por el usuario (el backend puede usarlo si lo soporta;
        // si no, resuelve por SYSDATE como hasta ahora — retrocompatible).
        anio,
        mes,
        nps: Number(this.kpiSelectors.find((k) => k.id === "nps")?.value ?? 0),
        ces: Number(this.kpiSelectors.find((k) => k.id === "ces")?.value ?? 0),
        csat: Number(
          this.kpiSelectors.find((k) => k.id === "csat")?.value ?? 0,
        ),
        respuestas: respuestasArray,
      };

      if (comentariosSeccionArray.length) {
        body.comentariosSeccion = comentariosSeccionArray;
      }

      if (this.contactoSeleccionadoId) {
        body.idContacto = this.contactoSeleccionadoId;
      }

      // Período a crear en paralelo (best-effort: si falla, se avisa pero la
      // encuesta se guarda igualmente — política de negocio definida).
      const periodoBody = {
        anio,
        mes,
        descripcion: `Periodo ${mm}-${anio}`,
      };

      this.loadingGuardar = true;
      try {
        const [ejecRes, periodoRes] = await Promise.allSettled([
          apiPost("/ejecuciones", body),
          apiPost("/periodo", periodoBody),
        ]);

        // 1) Resultado de la encuesta (crítico)
        if (ejecRes.status === "rejected") {
          throw ejecRes.reason || new Error("No se pudo guardar la encuesta");
        }
        const res = ejecRes.value;
        if (res.estatus !== "OK") {
          this.mostrarToast(`Error: ${res.mensaje || "no se pudo guardar"}`);
          return;
        }

        // 2) Resultado del período (no crítico)
        const pr = periodoRes.status === "fulfilled" ? periodoRes.value : null;
        const periodoError =
          periodoRes.status === "rejected" ||
          (pr && (pr.estatus === "ERROR" || pr.error));
        if (periodoError) {
          const yaExiste =
            pr &&
            JSON.stringify(pr).toLowerCase().match(/exist|duplicad|unique/);
          this.mostrarToast(
            yaExiste
              ? "Encuesta guardada ✔ (el período ya existía)"
              : "Encuesta guardada ✔ (no se pudo registrar el período)",
          );
        } else {
          this.mostrarToast(`Encuesta guardada exitosamente ✔`);
        }

        this.feedbackEjecucionId = res.idEjecucion;
        this.currentTab = "feedback";
        this.inicializarFeedback();
        window.scrollTo(0, 0);
        return;
      } catch (error) {
        console.error("Error guardando encuesta:", error);
        this.mostrarToast("Error al guardar la encuesta");
      } finally {
        this.loadingGuardar = false;
      }
    },

    // ============ FEEDBACK ============
    inicializarFeedback() {
      this.feedbackData = {};
      // Solo las secciones activas en esta captura
      this.seccionesActivas.forEach((sec) => {
        this.feedbackData[sec.seccion] = "";
      });
    },

    async guardarFeedback() {
      const feedback = [];
      const activas = new Set(this.seccionesActivas.map((s) => s.seccion));
      for (const [seccion, texto] of Object.entries(this.feedbackData)) {
        if (texto && texto.trim() && activas.has(seccion)) {
          feedback.push({
            seccion: seccion,
            feedback: texto.trim(),
          });
        }
      }

      if (!feedback.length) {
        if (
          !confirm(
            "No has agregado ningún feedback interno. ¿Deseas omitir este paso?",
          )
        ) {
          return;
        }
        this.saltarFeedback();
        return;
      }

      try {
        const url = `/ejecuciones/${this.feedbackEjecucionId}/feedback`;
        const res = await apiPost(url, { feedback });

        if (res.success === true || res.estatus === "OK") {
          this.mostrarToast("✅ Feedback guardado exitosamente");
          this.currentTab = "actual";
          this.cargarDashboard();
        } else {
          this.mostrarToast(
            `❌ Error: ${res.mensaje || res.message || "no se pudo guardar"}`,
          );
        }
      } catch (error) {
        console.error("Error guardando feedback:", error);
        this.mostrarToast("❌ Error al guardar el feedback");
      }
    },

    saltarFeedback() {
      if (confirm("¿Estás seguro de que deseas omitir el feedback interno?")) {
        this.currentTab = "actual";
        this.cargarDashboard();
      }
    },

    cancelarFeedback() {
      if (
        confirm(
          "¿Estás seguro de que deseas cancelar? El feedback no se guardará.",
        )
      ) {
        this.currentTab = "actual";
        this.cargarDashboard();
      }
    },

    // ============ HISTÓRICO ============
    async consultarHistorico() {
      if (!this.histFiltroCuenta) {
        this.mostrarToast("Selecciona un cliente");
        return;
      }
      if (!this.histFiltroEncuesta) {
        this.mostrarToast("Selecciona una encuesta");
        return;
      }

      let q = `/ejecuciones?idCuenta=${encodeURIComponent(
        this.histFiltroCuenta,
      )}&idEncuesta=${encodeURIComponent(this.histFiltroEncuesta)}`;
      if (this.histFiltroFecha) {
        q += `&desde=${encodeURIComponent(this.histFiltroFecha)}`;
      }

      this.loadingHist = true;
      try {
        const data = await apiGet(q);
        this.historicoEjecuciones = data.items || [];
        if (this.historicoEjecuciones.length === 0) {
          this.mostrarToast("No se encontraron ejecuciones con esos filtros");
        }
        this.histLevel = "lista";
      } catch (error) {
        console.error("Error consultando histórico:", error);
        this.mostrarToast("Error consultando ejecuciones");
      } finally {
        this.loadingHist = false;
      }
    },

    // Recarga la lista manteniendo el nivel actual (tras editar/eliminar)
    async recargarHistorico() {
      if (!this.histFiltroCuenta || !this.histFiltroEncuesta) return;
      let q = `/ejecuciones?idCuenta=${encodeURIComponent(
        this.histFiltroCuenta,
      )}&idEncuesta=${encodeURIComponent(this.histFiltroEncuesta)}`;
      if (this.histFiltroFecha) {
        q += `&desde=${encodeURIComponent(this.histFiltroFecha)}`;
      }
      try {
        const data = await apiGet(q);
        this.historicoEjecuciones = data.items || [];
      } catch (error) {
        console.error("Error recargando histórico:", error);
      }
    },

    // ============ EDICIÓN DE EJECUCIÓN ============
    async abrirEditar(exec) {
      if (!this.puedeEditar) {
        this.mostrarToast("No tienes permiso para editar");
        return;
      }
      const id = exec.idEjecucion || exec.idejecucion;
      if (!id) {
        this.mostrarToast("ID de ejecución no válido");
        return;
      }
      this.editEjecucionId = id;
      this.editMode = "cabecera";
      this.editForm = {
        nps: Number(exec.nps ?? exec.NPS ?? 0),
        ces: Number(exec.ces ?? exec.CES ?? 0),
        csat: Number(exec.csat ?? exec.CSAT ?? 0),
      };
      this.editRespuestas = {};
      this.editModalOpen = true;

      // Precargar el detalle para el modo "respuestas"
      this.loadingEdit = true;
      try {
        const d = await apiGet(`/ejecuciones/${id}`);
        const kpis = d.kpis || {};
        this.editForm = {
          nps: Number(kpis.nps ?? d.nps ?? this.editForm.nps ?? 0),
          ces: Number(kpis.ces ?? d.ces ?? this.editForm.ces ?? 0),
          csat: Number(kpis.csat ?? d.csat ?? this.editForm.csat ?? 0),
        };
        (d.respuestas || []).forEach((r) => {
          this.editRespuestas[r.idPregunta] =
            r.idOpcion != null ? r.idOpcion : r.comentario || "";
        });
      } catch (e) {
        console.error("Error cargando ejecución para editar:", e);
        this.mostrarToast("No se pudo cargar el detalle para editar");
      } finally {
        this.loadingEdit = false;
      }
    },

    cerrarEditar() {
      this.editModalOpen = false;
      this.editEjecucionId = null;
      this.editRespuestas = {};
    },

    async guardarEdicion() {
      if (!this.editEjecucionId) return;

      let body;
      if (this.editMode === "cabecera") {
        body = {
          nps: Number(this.editForm.nps),
          ces: Number(this.editForm.ces),
          csat: Number(this.editForm.csat),
        };
      } else {
        // Reemplazo de respuestas (recalcula ponderado en backend)
        const respuestas = [];
        for (const sec of this.encuestaSecciones) {
          for (const p of sec.preguntas || []) {
            const val = this.editRespuestas[p.idPregunta];
            if (p.tipo === "ABIERTA") {
              if (val && String(val).trim()) {
                respuestas.push({
                  idPregunta: p.idPregunta,
                  comentario: String(val).trim(),
                });
              }
            } else if (val != null && val !== "") {
              respuestas.push({
                idPregunta: p.idPregunta,
                idOpcion: Number(val),
              });
            }
          }
        }
        body = { nps: Number(this.editForm.nps), respuestas };
      }

      this.savingEdit = true;
      try {
        const res = await apiPut(`/ejecucion/${this.editEjecucionId}`, body);
        const ok =
          res == null ||
          res.estatus === "OK" ||
          res.success === true ||
          res.ok === true ||
          (!res.estatus && !res.error && !res.mensaje);
        if (ok) {
          this.mostrarToast("✅ Ejecución actualizada");
          this.cerrarEditar();
          await this.recargarHistorico();
        } else {
          this.mostrarToast(
            `❌ Error: ${res.mensaje || res.error || "no se pudo actualizar"}`,
          );
        }
      } catch (error) {
        console.error("Error actualizando ejecución:", error);
        this.mostrarToast(
          "❌ Error al actualizar (verifica que el endpoint PUT /ejecucion/:id exista)",
        );
      } finally {
        this.savingEdit = false;
      }
    },

    // ============ ELIMINACIÓN DE EJECUCIÓN ============
    abrirEliminar(exec) {
      if (!this.esAdmin) {
        this.mostrarToast("Solo un administrador puede eliminar");
        return;
      }
      const id = exec.idEjecucion || exec.idejecucion;
      if (!id) {
        this.mostrarToast("ID de ejecución no válido");
        return;
      }
      this.deleteEjecucion = exec;
      this.deleteTipo = "logico";
      this.deleteModalOpen = true;
    },

    cerrarEliminar() {
      this.deleteModalOpen = false;
      this.deleteEjecucion = null;
    },

    async confirmarEliminar() {
      if (!this.esAdmin) {
        this.mostrarToast("Solo un administrador puede eliminar");
        return;
      }
      const exec = this.deleteEjecucion;
      if (!exec) return;
      const id = exec.idEjecucion || exec.idejecucion;
      const path =
        this.deleteTipo === "fisico"
          ? `/ejecucion/${id}?fisico=true`
          : `/ejecucion/${id}`;

      this.deletingExec = true;
      try {
        const res = await apiDelete(path);
        const ok =
          res == null ||
          res.estatus === "OK" ||
          res.success === true ||
          res.ok === true ||
          (!res.estatus && !res.error && !res.mensaje);
        if (ok) {
          this.mostrarToast(
            this.deleteTipo === "fisico"
              ? "🗑️ Ejecución eliminada (físico)"
              : "🗑️ Ejecución eliminada (lógico)",
          );
          this.cerrarEliminar();
          await this.recargarHistorico();
        } else {
          this.mostrarToast(
            `❌ Error: ${res.mensaje || res.error || "no se pudo eliminar"}`,
          );
        }
      } catch (error) {
        console.error("Error eliminando ejecución:", error);
        this.mostrarToast(
          "❌ Error al eliminar (verifica que el endpoint DELETE /ejecucion/:id exista)",
        );
      } finally {
        this.deletingExec = false;
      }
    },

    // ============ ANÁLISIS EJECUTIVO (MISTRAL) ============
    // source: 'actual' (usa idEjecucion del dashboard) | 'hist' (usa historicoDetalle)
    async generarAnalisis(source) {
      this.analysisModalOpen = true;
      this.analysisLoading = true;
      this.analysisError = "";
      this.analysisResult = "";
      this.analysisCliente =
        source === "hist" && this.historicoDetalle
          ? this.historicoDetalle.cliente || ""
          : (this.dashboardData && this.dashboardData.cliente) || "";

      // Avanza los pasos cada ~3 s; el último queda activo hasta que responde el modelo.
      this.analysisStep = 0;
      clearInterval(this.analysisTimer);
      this.analysisTimer = setInterval(() => {
        if (this.analysisStep < this.analysisPasos.length - 1) {
          this.analysisStep += 1;
        }
      }, 3000);

      try {
        let payload = null;

        if (source === "hist" && this.historicoDetalle) {
          payload = {
            ...this.historicoDetalle,
            feedbackInterno: this.historicoFeedback || [],
          };
        } else if (this.dashboardData) {
          const idEjec =
            this.dashboardData.idEjecucion || this.dashboardData.idejecucion;
          if (idEjec) {
            // Trae el detalle completo (incluye respuestas y comentarios)
            const d = await apiGet(`/ejecuciones/${idEjec}`);
            payload = { ...d, feedbackInterno: this.feedbackItems || [] };
          } else {
            payload = {
              ...this.dashboardData,
              categorias: this.categorias || [],
              feedbackInterno: this.feedbackItems || [],
            };
          }
        }

        if (!payload) {
          this.analysisError = "No hay datos suficientes para analizar.";
          return;
        }

        const md = await analizarEncuesta(payload);
        if (!md) {
          this.analysisError = "El modelo no devolvió contenido.";
        } else {
          this.analysisResult = md;
        }
      } catch (error) {
        console.error("Error generando análisis:", error);
        const detalle =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Error desconocido";
        this.analysisError =
          "No se pudo generar el análisis. " +
          detalle;
      } finally {
        clearInterval(this.analysisTimer);
        this.analysisTimer = null;
        this.analysisLoading = false;
      }
    },

    cerrarAnalisis() {
      this.analysisModalOpen = false;
    },

    async copiarAnalisis() {
      try {
        await navigator.clipboard.writeText(this.analysisResult || "");
        this.mostrarToast("📋 Análisis copiado");
      } catch (e) {
        this.mostrarToast("No se pudo copiar");
      }
    },

    async abrirEjecucion(idEjecucion) {
      if (!idEjecucion) {
        this.mostrarToast("ID de ejecución no válido");
        return;
      }

      try {
        const d = await apiGet(`/ejecuciones/${idEjecucion}`);
        this.historicoDetalle = d;

        // Cargar feedback
        try {
          const fb = await apiGet(`/ejecuciones/${idEjecucion}/feedback`);
          this.historicoFeedback = fb.feedback || [];
        } catch (e) {
          this.historicoFeedback = [];
        }

        // Cargar comentarios del cliente
        this.historicoComentariosCliente = d.comentariosSeccion || [];

        // Contacto real que respondió esta ejecución (no el primero de la cuenta)
        this.historicoContacto = this.contactoDeEjecucion(d);

        this.histLevel = "detalle";
      } catch (error) {
        console.error("Error abriendo ejecución:", error);
        this.mostrarToast("Error abriendo la ejecución");
      }
    },

    // ============ DIRECTORIO ============
    aplicarFiltrosDirectorio() {
      let filtered = this.dirFiltroCuenta
        ? this.contactosAll.filter(
            (c) => (c.cuenta || c.CUENTA) === this.dirFiltroCuenta,
          )
        : this.contactosAll;

      if (this.dirModoActivo === "decision") {
        filtered = filtered.filter((c) => {
          const inf = (
            c.nivelInfluencia ||
            c.nivelinfluencia ||
            ""
          ).toUpperCase();
          return inf.includes("DECISION");
        });
      } else if (this.dirModoActivo === "promotor") {
        filtered = filtered.filter((c) => {
          const ev = (
            c.evaluacionCualitativa ||
            c.evaluacioncualitativa ||
            ""
          ).toUpperCase();
          return ev === "PROMOTOR";
        });
      } else if (this.dirModoActivo === "warning") {
        filtered = filtered.filter((c) => {
          const w = c.warnings || c.WARNINGS;
          return w != null && String(w).trim() !== "";
        });
      }

      this.dirContactosFiltrados = filtered;
    },

    toggleDirModo(modo) {
      this.dirModoActivo = this.dirModoActivo === modo ? null : modo;
      this.aplicarFiltrosDirectorio();
    },

    // ============ EXPORTAR DIRECTORIO A EXCEL ============
    // Exporta la lista mostrada (respeta el filtro de cuenta del spinner y el
    // modo activo). Si la cuenta es "Todas", exporta todos los registros.
    async descargarExcelDirectorio() {
      const lista = this.dirContactosFiltrados || [];
      if (!lista.length) {
        this.mostrarToast("No hay contactos para exportar");
        return;
      }
      try {
        const XLSX = await import("xlsx");
        const rows = lista.map((c) => ({
          Cuenta: c.cuenta || c.CUENTA || "",
          Nombre: c.nombre || "",
          Puesto: c.puesto || "",
          Email: c.email || "",
          Teléfono: c.telefono || "",
          "Nivel de influencia":
            c.nivelInfluencia || c.nivelinfluencia || "",
          Evaluación:
            c.evaluacionCualitativa || c.evaluacioncualitativa || "",
          Warnings: c.warnings || c.WARNINGS || "",
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        ws["!cols"] = [
          { wch: 24 }, { wch: 26 }, { wch: 26 }, { wch: 28 },
          { wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 30 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Directorio CX");

        const cuentaNom = this.dirFiltroCuenta || "Todos";
        const fecha = new Date().toISOString().split("T")[0];
        const safe = String(cuentaNom)
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .replace(/[^\w-]+/g, "_")
          .slice(0, 40);
        XLSX.writeFile(wb, `Directorio_CX_${safe}_${fecha}.xlsx`);
        this.mostrarToast(`✅ Excel descargado (${lista.length} registros)`);
      } catch (error) {
        console.error("Error exportando Excel:", error);
        this.mostrarToast("❌ Error al generar el Excel");
      }
    },

    // ============ EXPORTAR PDF ============
    async exportarPDF() {
      if (!this.historicoDetalle) {
        this.mostrarToast("No hay datos para exportar");
        return;
      }

      const btn =
        document.querySelector(".header-actions .btn-custom.secondary") ||
        document.querySelector(".btn-custom.secondary");
      const originalText = btn ? btn.textContent : "";
      if (btn) {
        btn.textContent = "Generando PDF...";
        btn.disabled = true;
      }

      try {
        const d = this.historicoDetalle;
        const enc = d.encuesta || {};
        const kpis = d.kpis || {};

        // ---- Documento A4, unidades en mm ----
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        const PW = 210;
        const PH = 297;
        const M = 20; // margen (>= 20mm arriba/abajo/lados, evita recortes)
        const CW = PW - M * 2;
        const BOTTOM = PH - M;
        const footerSpace = 14;

        // ---- Paleta corporativa ----
        const C = {
          primary: [0, 60, 165],
          primaryDark: [0, 42, 122],
          secondary: [67, 178, 52],
          ink: [10, 22, 40],
          dim: [74, 90, 122],
          faint: [128, 144, 176],
          line: [222, 230, 244],
          soft: [240, 244, 250],
        };
        const semRGB = (s) => {
          const u = (s || "").toString().toUpperCase();
          if (u.includes("VERDE") || u.includes("GREEN")) return [67, 178, 52];
          if (u.includes("AMAR") || u.includes("YELLOW")) return [230, 160, 30];
          if (u.includes("ROJO") || u.includes("RED")) return [255, 93, 115];
          return [128, 144, 176];
        };
        const norm = (s) => this.normalizaSeccion(s);

        // ---- Cursor / control de páginas ----
        let y = 0;
        let started = false;
        let pageBaseTitle = "";

        const drawHeaderBand = (title) => {
          pdf.setFillColor.apply(pdf, C.primary);
          pdf.roundedRect(M, M, CW, 12, 2, 2, "F");
          pdf.setFillColor.apply(pdf, C.secondary);
          pdf.rect(M, M, 3, 12, "F");
          pdf.setTextColor(255, 255, 255);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(12);
          pdf.text(String(title), M + 8, M + 8);
          y = M + 12 + 8;
        };
        const addPhysicalPage = (title) => {
          if (started) pdf.addPage();
          started = true;
          drawHeaderBand(title);
        };
        const startPage = (title) => {
          pageBaseTitle = title;
          addPhysicalPage(title);
        };
        const ensure = (h) => {
          if (y + h > BOTTOM - footerSpace) {
            addPhysicalPage(pageBaseTitle + " (cont.)");
          }
        };
        const gap = (h) => {
          y += h;
        };

        // ---- Texto con salto de línea y avance de cursor ----
        const txt = (s, x, opts) => {
          const o = opts || {};
          const size = o.size || 9.5;
          const style = o.style || "normal";
          const color = o.color || C.ink;
          const maxW = o.maxW || CW - (x - M);
          const lh = o.lh || 4.6;
          pdf.setFont("helvetica", style);
          pdf.setFontSize(size);
          pdf.setTextColor.apply(pdf, color);
          const lines = pdf.splitTextToSize(String(s == null ? "" : s), maxW);
          ensure(lines.length * lh);
          pdf.text(lines, x, y);
          y += lines.length * lh;
        };

        // ---- Caja de comentario (texto en cursiva con barra lateral) ----
        const commentBox = (text) => {
          const lines = pdf.splitTextToSize(String(text), CW - 8);
          const boxH = lines.length * 4.4 + 5;
          ensure(boxH);
          pdf.setFillColor.apply(pdf, C.soft);
          pdf.roundedRect(M, y - 1, CW, boxH, 1.5, 1.5, "F");
          pdf.setFillColor.apply(pdf, C.secondary);
          pdf.rect(M, y - 1, 2, boxH, "F");
          pdf.setFont("helvetica", "italic");
          pdf.setFontSize(8.8);
          pdf.setTextColor.apply(pdf, C.dim);
          pdf.text(lines, M + 5, y + 3.5);
          y += boxH + 3;
        };

        // ---- Encabezado de sección con score/semáforo ----
        const sectionHeader = (name) => {
          const cat = (d.categorias || []).find(
            (c) => norm(c.seccion) === norm(name),
          );
          ensure(12);
          pdf.setFillColor.apply(pdf, C.soft);
          pdf.roundedRect(M, y - 1, CW, 9, 1.5, 1.5, "F");
          const rgb = semRGB(cat && cat.semaforo);
          pdf.setFillColor.apply(pdf, rgb);
          pdf.rect(M, y - 1, 2.5, 9, "F");
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(10.5);
          pdf.setTextColor.apply(pdf, C.primaryDark);
          pdf.text(String(name).toUpperCase(), M + 6, y + 5);
          if (cat && cat.score != null) {
            pdf.setTextColor.apply(pdf, rgb);
            pdf.text(cat.score + "%", M + CW - 4, y + 5, { align: "right" });
          }
          y += 12;
        };

        // ---- Bloque de una pregunta/respuesta ----
        const questionBlock = (r, idx) => {
          const num =
            r.numPregunta != null ? r.numPregunta + ". " : idx + 1 + ". ";
          txt(num + (r.texto || ""), M, {
            size: 9.5,
            style: "bold",
            color: C.ink,
          });
          if (r.tipo === "ABIERTA") {
            txt("Respuesta abierta:", M, { size: 8.5, color: C.dim, lh: 4.2 });
            commentBox('"' + ((r.comentario || "-").trim() || "-") + '"');
          } else {
            const opcion = r.opcion || "-";
            const valor = r.valorCapturado != null ? r.valorCapturado : "-";
            txt("Respuesta: " + opcion + "     -     Valor: " + valor, M + 3, {
              size: 9,
              color: C.dim,
            });
            if (r.comentario && r.comentario.trim()) {
              txt('"' + r.comentario.trim() + '"', M + 3, {
                size: 8.5,
                style: "italic",
                color: C.faint,
              });
            }
            y += 1.5;
          }
          ensure(4);
          pdf.setDrawColor.apply(pdf, C.line);
          pdf.setLineWidth(0.2);
          pdf.line(M, y, M + CW, y);
          y += 4;
        };

        // ---- Página de respuestas con uno o varios grupos ----
        const renderResponsePage = (title, groups) => {
          const total = groups.reduce((a, g) => a + g.rows.length, 0);
          if (total === 0) return; // omitir página sin respuestas
          startPage(title);
          groups.forEach((g) => {
            if (!g.rows.length) return;
            sectionHeader(g.name);
            g.rows.forEach((r, i) => questionBlock(r, i));
            gap(2);
          });
        };

        // ===== PÁGINA 1: Portada + metadata + Categorías =====
        startPage("Reporte de Ejecución");
        const meta = [
          ["Cliente", d.cliente || "-"],
          [
            "Encuesta",
            (
              (enc.nombre || "") +
              (enc.clave ? " - " + enc.clave + " v" + (enc.version || "") : "")
            ).trim() || "-",
          ],
          ["Período", d.periodo || "-"],
          ["Fecha de aplicación", d.fechaAplicacion || "-"],
          ["ID de ejecución", String(d.idEjecucion != null ? d.idEjecucion : "-")],
        ];
        meta.forEach((kv) => {
          ensure(6);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(9);
          pdf.setTextColor.apply(pdf, C.faint);
          pdf.text(kv[0] + ":", M, y);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor.apply(pdf, C.ink);
          pdf.text(String(kv[1]), M + 42, y);
          y += 6;
        });
        gap(4);

        // KPIs
        const score = Math.round(this.getScorePct(d));
        const sem = d.semaforo || "-";
        const npsV = kpis.nps != null ? kpis.nps : d.nps != null ? d.nps : "-";
        const cesV = kpis.ces != null ? kpis.ces : d.ces != null ? d.ces : "-";
        const csatV =
          kpis.csat != null ? kpis.csat : d.csat != null ? d.csat : "-";
        const nums = [npsV, cesV, csatV].filter((v) => typeof v === "number");
        const prom = nums.length
          ? (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1)
          : "-";
        const cards = [
          { label: "SCORE GLOBAL", value: score + "%", sub: sem, color: semRGB(sem) },
          { label: "NPS", value: String(npsV), sub: "Net Promoter", color: C.primary },
          { label: "CES", value: String(cesV), sub: "Customer Effort", color: C.primary },
          { label: "CSAT", value: String(csatV), sub: "Satisfaction", color: C.primary },
        ];
        ensure(26);
        const cardW = (CW - 9) / 4;
        cards.forEach((c, i) => {
          const x = M + i * (cardW + 3);
          pdf.setFillColor.apply(pdf, C.soft);
          pdf.roundedRect(x, y, cardW, 24, 2, 2, "F");
          pdf.setDrawColor.apply(pdf, c.color);
          pdf.setLineWidth(0.6);
          pdf.roundedRect(x, y, cardW, 24, 2, 2, "S");
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(7);
          pdf.setTextColor.apply(pdf, C.faint);
          pdf.text(c.label, x + cardW / 2, y + 5, { align: "center" });
          pdf.setFontSize(16);
          pdf.setTextColor.apply(pdf, c.color);
          pdf.text(c.value, x + cardW / 2, y + 14, { align: "center" });
          pdf.setFontSize(6.5);
          pdf.setTextColor.apply(pdf, C.faint);
          pdf.text(String(c.sub), x + cardW / 2, y + 20, { align: "center" });
        });
        y += 24 + 6;
        ensure(7);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor.apply(pdf, C.primaryDark);
        pdf.text("KPI CX Promedio: " + prom, M, y);
        y += 9;

        // Categorías evaluadas
        ensure(10);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor.apply(pdf, C.primaryDark);
        pdf.text("Categorías Evaluadas", M, y);
        y += 7;
        const cats = d.categorias || [];
        if (!cats.length) {
          txt("Sin categorías registradas.", M, {
            color: C.faint,
            style: "italic",
          });
        } else {
          cats.forEach((c) => {
            ensure(12);
            const rgb = semRGB(c.semaforo);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9);
            pdf.setTextColor.apply(pdf, C.ink);
            pdf.text(String(c.seccion || "-"), M, y);
            pdf.setTextColor.apply(pdf, rgb);
            pdf.text((c.score != null ? c.score : 0) + "%", M + CW, y, {
              align: "right",
            });
            y += 2.5;
            pdf.setFillColor.apply(pdf, C.line);
            pdf.roundedRect(M, y, CW, 3, 1.5, 1.5, "F");
            const w =
              (Math.max(0, Math.min(100, Number(c.score || 0))) / 100) * CW;
            if (w > 0) {
              pdf.setFillColor.apply(pdf, rgb);
              pdf.roundedRect(M, y, w, 3, 1.5, 1.5, "F");
            }
            y += 8;
          });
        }

        // ===== PÁGINAS de Respuestas =====
        const respuestas = d.respuestas || [];
        const inSec = (r, key) => norm(r.seccion).includes(key);
        const comercial = respuestas.filter((r) => inSec(r, "COMERCIAL"));
        const desempeno = respuestas.filter((r) => inSec(r, "DESEMPENO"));
        const instal = respuestas.filter((r) => inSec(r, "INSTALACION"));
        const sistemas = respuestas.filter((r) => inSec(r, "SISTEMA"));
        const transporte = respuestas.filter((r) => inSec(r, "TRANSPORTE"));

        // Página 2: Comercial y Desempeño Operativo completos (preguntas 1-9)
        renderResponsePage("Respuestas - Comercial y Desempeño Operativo", [
          { name: "Comercial", rows: comercial },
          { name: "Desempeño Operativo", rows: desempeno },
        ]);
        renderResponsePage("Respuestas - Instalaciones y Sistemas", [
          { name: "Instalaciones", rows: instal },
          { name: "Sistemas", rows: sistemas },
        ]);
        renderResponsePage("Respuestas - Transporte", [
          { name: "Transporte", rows: transporte },
        ]);

        // ===== PÁGINA 6: Comentarios por sección =====
        const comentarios =
          this.historicoComentariosCliente &&
          this.historicoComentariosCliente.length
            ? this.historicoComentariosCliente
            : d.comentariosSeccion || [];
        const comGroups = {};
        comentarios.forEach((c) => {
          const sec = c.seccion || "Sin sección";
          const t = (c.comentario || c.COMENTARIO || "").trim();
          if (t) {
            if (!comGroups[sec]) comGroups[sec] = [];
            comGroups[sec].push(t);
          }
        });
        if (Object.keys(comGroups).length) {
          startPage("Comentarios por Sección");
          Object.keys(comGroups).forEach((sec) => {
            sectionHeader(sec);
            comGroups[sec].forEach((c, i) => {
              txt("Comentario " + (i + 1), M, { size: 8, color: C.faint });
              commentBox('"' + c + '"');
            });
            gap(2);
          });
        }

        // ===== PÁGINA 7: Feedback interno =====
        const feedback = this.historicoFeedback || [];
        if (feedback.length) {
          startPage("Feedback Interno del Equipo");
          feedback.forEach((item) => {
            ensure(10);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9.5);
            pdf.setTextColor.apply(pdf, C.primaryDark);
            pdf.text(String(item.seccion || "Sin sección"), M, y);
            if (item.fecha) {
              pdf.setFont("helvetica", "normal");
              pdf.setFontSize(8);
              pdf.setTextColor.apply(pdf, C.faint);
              pdf.text(String(item.fecha), M + CW, y, { align: "right" });
            }
            y += 5;
            txt(item.feedback || "-", M, { size: 9, color: C.dim });
            gap(2);
            ensure(4);
            pdf.setDrawColor.apply(pdf, C.line);
            pdf.line(M, y, M + CW, y);
            y += 4;
          });
        }

        // ===== Pies de página: numeración X de N =====
        const totalPages = pdf.getNumberOfPages();
        const hoy = new Date().toLocaleDateString("es-MX", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
        for (let p = 1; p <= totalPages; p++) {
          pdf.setPage(p);
          pdf.setDrawColor.apply(pdf, C.line);
          pdf.setLineWidth(0.2);
          pdf.line(M, PH - 14, PW - M, PH - 14);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8);
          pdf.setTextColor.apply(pdf, C.primary);
          pdf.text("CX Dashboard", M, PH - 9);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor.apply(pdf, C.faint);
          pdf.text("Página " + p + " de " + totalPages, PW / 2, PH - 9, {
            align: "center",
          });
          pdf.text(hoy, PW - M, PH - 9, { align: "right" });
        }

        // ===== Descarga automática =====
        const idEj = d.idEjecucion != null ? d.idEjecucion : "reporte";
        const fArch = new Date().toISOString().split("T")[0];
        pdf.save("CX_Dashboard_Ejecucion_" + idEj + "_" + fArch + ".pdf");
        track("reporte_generado", {
          tipo: "pdf_dashboard",
          idEjecucion: d.idEjecucion != null ? d.idEjecucion : null,
          paginas: totalPages,
        });

        this.mostrarToast("✅ PDF exportado exitosamente");
      } catch (error) {
        console.error("Error al exportar PDF:", error);
        this.mostrarToast("❌ Error al generar el PDF");
      } finally {
        if (btn) {
          btn.textContent = originalText || "📄 Exportar a PDF";
          btn.disabled = false;
        }
      }
    },
  },
};
</script>

<style scoped>
/* ============================================
   VARIABLES Y RESET
   ============================================ */
:root {
  --primary: #003ca5;
  --primary-dark: #002a7a;
  --primary-light: #1a5cc9;
  --secondary: #43b234;
  --secondary-light: #5cc94e;
  --white: #ffffff;
  --gray-100: #f0f4fa;
  --gray-200: #d4dff0;
  --gray-300: #a0b8d8;
  --ink: #0a1628;
  --ink2: #1a2a4a;
  --surface: rgba(0, 0, 0, 0.04);
  --surface-2: rgba(0, 0, 0, 0.06);
  --border: rgba(0, 0, 0, 0.1);
  --border-2: rgba(0, 0, 0, 0.16);
  --txt: #0a1628;
  --txt-dim: #4a5a7a;
  --txt-faint: #8090b0;
  --good: #43b234;
  --lime: #5cc94e;
  --warn: #ffc24b;
  --bad: #ff5d73;
  --accent: #003ca5;
  --accent2: #1a5cc9;
  --font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial,
    sans-serif;
  --mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.08);
  --shadow-md: 0 8px 32px rgba(0, 0, 0, 0.12);
  --shadow-lg: 0 16px 48px rgba(0, 0, 0, 0.18);
}

/* ============================================
   ESTILOS GENERALES
   ============================================ */
* {
  box-sizing: border-box;
}

main {
  position: relative;
  z-index: 1;
  max-width: 1400px; /* mismo ancho que DashboardGeneral (.dg) */
  margin: 0 auto;
  padding: clamp(16px, 3vw, 32px);
}

/* ============================================
   TABS
   ============================================ */
.tabs-custom {
  display: flex;
  gap: 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 4px;
  margin: 4px 0 2px;
  width: fit-content;
  flex-wrap: wrap;
}

.tab-custom {
  font: inherit;
  font-weight: 600;
  font-size: clamp(12px, 1.1vw, 14px);
  color: var(--txt-dim);
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 20px;
  border-radius: 10px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.tab-custom:hover {
  color: var(--txt);
  background: var(--surface-2);
}

.tab-custom.active {
  color: var(--on-accent);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  box-shadow: 0 4px 16px rgba(67, 178, 52, 0.25);
}

/* ============================================
   ROW / GRID
   ============================================ */
.row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
}

.row > * {
  flex: 1 1 auto;
  min-width: 180px;
}

.grid {
  display: grid;
  gap: 16px;
}

.hero {
  grid-template-columns: 1.2fr 1fr;
  align-items: stretch;
  margin-top: 16px;
}

.kpi-grid {
  grid-template-columns: repeat(2, 2fr);
}

/* ============================================
   LABELS Y INPUTS
   ============================================ */
label {
  display: block;
  font-size: 11px;
  color: var(--txt-dim);
  margin-bottom: 4px;
  font-weight: 600;
  letter-spacing: 0.05em;
}

select,
input,
textarea {
  font: inherit;
  padding: 10px 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--white);
  color: var(--txt);
  width: 100%;
  outline: none;
  transition: all 0.3s ease;
}

select {
  min-width: 200px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%234A5A7A' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;
}

select option {
  background: var(--white);
  color: var(--txt);
}

select:focus,
input:focus,
textarea:focus {
  border-color: var(--secondary);
  box-shadow: 0 0 0 3px rgba(67, 178, 52, 0.15);
}

/* ============================================
   BOTONES
   ============================================ */
.btn-custom {
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: var(--on-accent);
  border: none;
  border-radius: 12px;
  padding: 11px 24px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 60, 165, 0.25);
  letter-spacing: 0.02em;
  font-size: 14px;
  white-space: nowrap;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-custom:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(0, 60, 165, 0.35);
  color: var(--on-accent);
}

.btn-custom:active {
  transform: translateY(0);
}

.btn-custom.secondary {
  background: var(--surface);
  color: var(--txt);
  border: 1px solid var(--border);
  box-shadow: none;
}

.btn-custom.secondary:hover {
  background: var(--surface-2);
  border-color: var(--border-2);
  color: var(--txt);
}

.btn-custom:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* ============================================
   CARDS
   ============================================ */
.card-custom {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: clamp(16px, 2.5vw, 24px);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.card-custom:hover {
  border-color: var(--border-2);
  box-shadow: var(--shadow-md);
}

.label-custom {
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--txt-faint);
  font-weight: 700;
  margin-bottom: 4px;
}

.sec-title {
  font-size: clamp(16px, 2vw, 20px);
  font-weight: 700;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.sec-title .bar-accent {
  width: 4px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(180deg, var(--secondary), var(--primary));
  flex-shrink: 0;
}

.sec-sub {
  font-size: clamp(12px, 1.2vw, 14px);
  color: var(--txt-dim);
  margin-bottom: 14px;
  margin-left: 14px;
}

.muted {
  color: var(--txt-dim);
}

.section-main-title {
  font-size: clamp(18px, 2.5vw, 24px);
  font-weight: 700;
}

/* ============================================
   GAUGE
   ============================================ */
.gauge-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  background: linear-gradient(180deg, rgba(67, 178, 52, 0.04), var(--white));
}

.gauge-svg {
  width: 100%;
  max-width: 340px;
  margin: 0 auto;
}

.gauge-num {
  font-family: var(--mono);
  font-weight: 700;
  font-size: clamp(40px, 6vw, 60px);
  line-height: 1;
  letter-spacing: -2px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.gauge-num small {
  font-size: 0.4em;
  color: var(--txt-dim);
  -webkit-text-fill-color: var(--txt-dim);
  margin-left: 2px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 20px;
  border-radius: 999px;
  font-weight: 700;
  font-size: clamp(13px, 1.2vw, 15px);
  border: 1px solid var(--border-2);
  background: var(--white);
}

.gauge-cap {
  font-size: clamp(12px, 1.1vw, 14px);
  color: var(--txt-dim);
  margin-top: 12px;
  line-height: 1.6;
}

.gauge-cap b {
  color: var(--txt);
  font-size: clamp(15px, 1.3vw, 18px);
}

/* ============================================
   KPI RINGS
   ============================================ */
.kpi {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: clamp(14px, 2vw, 20px) clamp(10px, 1.5vw, 16px);
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.kpi:hover {
  transform: translateY(-4px);
  border-color: var(--border-2);
  box-shadow: var(--shadow-md);
}

.kpi h3 {
  font-size: clamp(13px, 1.2vw, 16px);
  font-weight: 700;
  margin-top: 6px;
}

.kpi .desc {
  font-size: clamp(10px, 1vw, 12px);
  color: var(--txt-dim);
  margin-top: 4px;
  line-height: 1.4;
  min-height: 28px;
}

.kpi-ring {
  transition: stroke-dashoffset 1.6s cubic-bezier(0.18, 1, 0.3, 1);
}

.kpi-value-text {
  font-family: var(--mono);
  font-weight: 600;
  font-size: 26px;
}

.kpi-label-text {
  font-size: 11px;
}

/* ============================================
   BARRAS DE CATEGORÍA
   ============================================ */
.cat-row {
  margin-bottom: 16px;
}

.cat-row:last-child {
  margin-bottom: 0;
}

.cat-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 6px;
  gap: 12px;
}

.cat-name {
  font-size: clamp(14px, 1.2vw, 16px);
  font-weight: 600;
}

.cat-val {
  font-family: var(--mono);
  font-weight: 600;
  font-size: clamp(16px, 1.3vw, 20px);
}

.bar {
  height: 8px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.06);
  overflow: hidden;
  position: relative;
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: 8px;
  width: 0;
  transition: width 1.2s cubic-bezier(0.16, 1, 0.3, 1);
}

/* ============================================
   INSIGHT
   ============================================ */
.insight {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: linear-gradient(
    120deg,
    rgba(0, 60, 165, 0.06),
    rgba(67, 178, 52, 0.04)
  );
  border: 1px solid rgba(0, 60, 165, 0.12);
}

.insight .ic {
  font-size: 28px;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 2px;
}

.insight h4 {
  font-size: clamp(15px, 1.2vw, 17px);
  font-weight: 700;
  margin-bottom: 4px;
}

.insight p {
  font-size: clamp(13px, 1.1vw, 15px);
  color: var(--txt-dim);
  line-height: 1.6;
}

/* ============================================
   CONTACTOS
   ============================================ */
.contact-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px 20px;
  font-size: 13px;
}

.contact-label {
  color: var(--txt-faint);
}

.contact-value {
  font-weight: 500;
}

.contact-card-info {
  margin-top: 12px;
  padding: 16px;
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border);
}

.contact-card-title {
  font-weight: 600;
  font-size: 14px;
  color: var(--txt);
  margin-bottom: 8px;
}

.contact-accent {
  background: linear-gradient(
    180deg,
    var(--primary),
    var(--secondary)
  ) !important;
}

/* ============================================
   FEEDBACK
   ============================================ */
.feedback-accent {
  background: linear-gradient(180deg, var(--warn), var(--accent)) !important;
}

.comments-accent {
  background: linear-gradient(180deg, var(--good), var(--accent)) !important;
}

.feedback-count {
  margin-left: auto;
  font-size: 11px;
  font-weight: 400;
  color: var(--txt-faint);
}

.feedback-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
}

.feedback-item-header {
  min-width: 140px;
  font-weight: 600;
  font-size: 13px;
  color: var(--txt);
}

.feedback-item-body {
  flex: 1;
  font-size: 13px;
  color: var(--txt-dim);
  background: rgba(255, 194, 75, 0.08);
  padding: 6px 12px;
  border-radius: 8px;
  border-left: 3px solid var(--warn);
}

.feedback-item-date {
  font-size: 11px;
  color: var(--txt-faint);
  white-space: nowrap;
}

.feedback-info-banner {
  background: #f0f7ff;
  padding: 14px 18px;
  border-radius: 12px;
  margin-bottom: 16px;
  border-left: 4px solid #003ca5;
}

.feedback-info-title {
  font-weight: 600;
  color: var(--heading);
  font-size: 14px;
}

.feedback-info-text {
  font-size: 13px;
  color: var(--txt-dim);
  margin-top: 4px;
}

/* ============================================
   COMENTARIOS
   ============================================ */
.comment-group {
  margin-bottom: 16px;
  border-left: 3px solid var(--primary);
  padding-left: 12px;
}

.comment-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border);
}

.comment-group-title {
  font-weight: 700;
  font-size: 14px;
  color: var(--txt);
}

.comment-group-count {
  font-size: 11px;
  color: var(--txt-faint);
}

.comment-item {
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
}

.comment-item-last {
  border-bottom: none;
}

.comment-item-number {
  font-size: 11px;
  color: var(--txt-faint);
  margin-bottom: 2px;
}

.comment-item-text {
  font-size: 13px;
  color: var(--txt);
  background: var(--surface-2);
  padding: 10px 14px;
  border-radius: 8px;
  border-left: 3px solid var(--good);
}

/* ============================================
   SECCIONES DE CAPTURA
   ============================================ */
.sec {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: clamp(14px, 2vw, 20px);
  margin-bottom: 14px;
  box-shadow: var(--shadow-sm);
}

.sec h3 {
  margin: 0 0 4px;
  font-size: clamp(15px, 1.3vw, 18px);
}

.sec .w {
  font-size: 12px;
  color: var(--txt-faint);
}

.q {
  padding: 14px 0;
  border-top: 1px solid var(--border);
}

.q:first-of-type {
  border-top: none;
}

.q p {
  margin: 0 0 8px;
  font-weight: 500;
  font-size: clamp(14px, 1.1vw, 16px);
}

.opts {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.radio-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 6px 14px;
  cursor: pointer;
  margin: 0;
  color: var(--txt);
  background: var(--white);
  font-size: clamp(12px, 1vw, 14px);
  transition: all 0.2s ease;
}

.radio-label:hover {
  border-color: var(--border-2);
  background: var(--surface);
}

.opts input[type="radio"] {
  width: auto;
  margin-right: 2px;
}

/* ============================================
   KPI SELECTOR (Botones 0-10)
   ============================================ */
.kpi-selector {
  flex: 1;
  min-width: 120px;
}

.kpi-selector label {
  display: block;
  font-size: 12px;
  color: var(--txt-dim);
  margin-bottom: 6px;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.kpi-btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  background: var(--surface);
  border-radius: 10px;
  padding: 6px;
  border: 1px solid var(--border);
  min-height: 44px;
}

.kpis-mini {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.kpis-mini > div {
  flex: 1;
  min-width: 100px;
}

.btn-option {
  flex: 1 0 auto;
  min-width: 28px;
  max-width: 36px;
  height: 32px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--white);
  color: var(--txt);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--mono);
  user-select: none;
}

.btn-option:hover {
  border-color: var(--border-2);
  background: var(--surface-2);
  transform: translateY(-1px);
}

.btn-option:active {
  transform: scale(0.95);
}

.btn-option.selected {
  border-color: var(--secondary);
  background: var(--secondary);
  color: var(--on-accent);
  box-shadow: 0 2px 8px rgba(67, 178, 52, 0.3);
}

.btn-option.selected:hover {
  background: var(--secondary-light);
  border-color: var(--secondary-light);
}

/* ============================================
   FEEDBACK FORM
   ============================================ */
.feedback-section {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: clamp(14px, 2vw, 20px);
  margin-bottom: 14px;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
}

.feedback-section:hover {
  border-color: var(--border-2);
  box-shadow: var(--shadow-md);
}

.feedback-section .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
}

.feedback-section .section-header h4 {
  font-size: clamp(15px, 1.3vw, 17px);
  font-weight: 700;
  color: var(--txt);
  margin: 0;
}

.feedback-section .section-header .score-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  background: var(--surface);
}

.feedback-section textarea {
  width: 100%;
  min-height: 80px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--white);
  color: var(--txt);
  font-family: var(--font);
  font-size: 14px;
  resize: vertical;
  transition: all 0.3s ease;
}

.feedback-section textarea:focus {
  border-color: var(--secondary);
  box-shadow: 0 0 0 3px rgba(67, 178, 52, 0.15);
  outline: none;
}

.feedback-section textarea::placeholder {
  color: var(--txt-faint);
}

.feedback-counter {
  font-size: 12px;
  color: var(--txt-faint);
  text-align: right;
  margin-top: 4px;
}

/* ============================================
   ENCUESTAS HISTÓRICO
   ============================================ */
.crumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: clamp(12px, 1vw, 14px);
  color: var(--txt-faint);
  margin: 0 0 14px;
}

.crumbs a {
  color: var(--brand-text);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s;
  font-weight: 500;
}

.crumbs a:hover {
  color: var(--heading);
  text-decoration: underline;
}

.crumbs .sep {
  opacity: 0.4;
  margin: 0 2px;
}

.crumbs .cur {
  color: var(--txt);
  font-weight: 600;
}

.filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-top: 4px;
}

.filters > div {
  flex: 1;
  min-width: 180px;
}

.exec-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.exec-row {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  cursor: pointer;
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: clamp(12px, 1.5vw, 16px);
  transition: all 0.3s ease;
  flex-wrap: wrap;
  box-shadow: var(--shadow-sm);
}

.exec-row:hover {
  border-color: var(--border-2);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.exec-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.exec-date {
  font-weight: 700;
  font-size: clamp(14px, 1.2vw, 16px);
}

.exec-sub {
  font-size: clamp(11px, 1vw, 13px);
  color: var(--txt-dim);
}

.exec-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.exec-kpis {
  display: flex;
  gap: 8px;
  font-family: var(--mono);
  font-size: clamp(11px, 1vw, 13px);
  color: var(--txt-dim);
}

.exec-score {
  font-family: var(--mono);
  font-weight: 600;
  font-size: clamp(18px, 1.5vw, 22px);
  min-width: 56px;
  text-align: right;
}

.exec-chevron {
  color: var(--txt-faint);
  font-size: 20px;
  transition: transform 0.3s;
}

.exec-row:hover .exec-chevron {
  transform: translateX(4px);
}

.pill-sem {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: clamp(11px, 1vw, 13px);
  font-weight: 700;
  border: 1px solid var(--border-2);
}

.header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

/* ============================================
   RESPONSES
   ============================================ */
.resp-group {
  margin-bottom: 16px;
}

.resp-group:last-child {
  margin-bottom: 0;
}

.resp-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 0 0 8px;
  font-weight: 700;
  font-size: clamp(14px, 1.2vw, 16px);
  border-bottom: 1px solid var(--border);
  padding-bottom: 6px;
}

.resp-row {
  display: grid;
  grid-template-columns: 2.2fr auto auto;
  gap: 6px 12px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
}

.resp-row:last-child {
  border-bottom: none;
}

.resp-q {
  font-size: clamp(12px, 1vw, 14px);
  line-height: 1.5;
}

.resp-q .qn {
  font-family: var(--mono);
  color: var(--txt-faint);
  margin-right: 6px;
}

.resp-q .tipo {
  font-size: 10px;
  color: var(--txt-faint);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-left: 6px;
}

.resp-opt {
  font-size: clamp(12px, 1vw, 14px);
  color: var(--txt);
  text-align: right;
}

.resp-val {
  font-family: var(--mono);
  font-weight: 600;
  font-size: clamp(13px, 1.1vw, 15px);
  text-align: right;
  min-width: 40px;
}

.resp-com {
  grid-column: 1 / -1;
  font-size: clamp(12px, 1vw, 14px);
  color: var(--txt-dim);
  font-style: italic;
  line-height: 1.5;
  margin-top: 4px;
}

/* ============================================
   DIRECTORIO CX
   ============================================ */
.dir-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin: 4px 0 18px;
}

.stat {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: clamp(14px, 2vw, 18px);
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.stat .n {
  font-family: var(--mono);
  font-size: clamp(28px, 3vw, 36px);
  font-weight: 700;
  line-height: 1.2;
}

.stat .l {
  font-size: clamp(10px, 1vw, 12px);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--txt-faint);
  margin-top: 4px;
  font-weight: 600;
}

.stat.warn {
  background: linear-gradient(120deg, rgba(255, 194, 75, 0.06), var(--white));
  border-color: rgba(255, 194, 75, 0.2);
}

.stat.warn .n {
  color: var(--warn);
}

.stat.clickable {
  cursor: pointer;
}

.stat.clickable:hover {
  border-color: var(--border-2);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.stat.active {
  border-color: var(--secondary);
  box-shadow: 0 0 0 2px var(--secondary), var(--shadow-sm);
}

.stat.active .l {
  color: var(--secondary);
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}

.contact-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: clamp(14px, 2vw, 18px);
  transition: all 0.3s ease;
  position: relative;
  box-shadow: var(--shadow-sm);
}

.contact-card:hover {
  border-color: var(--border-2);
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
}

.contact-card.warn {
  border-color: rgba(255, 194, 75, 0.3);
  background: linear-gradient(120deg, rgba(255, 194, 75, 0.04), var(--white));
}

.cc-name {
  font-size: clamp(15px, 1.3vw, 18px);
  font-weight: 700;
}

.cc-puesto {
  font-size: clamp(12px, 1vw, 14px);
  color: var(--txt-dim);
  margin-top: 2px;
}

.cc-cuenta {
  font-size: clamp(11px, 1vw, 13px);
  color: var(--secondary);
  margin-top: 4px;
  font-weight: 600;
}

.cc-row {
  display: flex;
  gap: 6px;
  font-size: clamp(12px, 1vw, 14px);
  color: var(--txt-dim);
  margin-top: 6px;
  word-break: break-word;
}

.cc-row .k {
  color: var(--txt-faint);
  min-width: 60px;
  flex-shrink: 0;
}

.cc-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.tag-inf {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--txt-dim);
}

.eval-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid var(--border-2);
}

.cc-warn {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 10px;
  font-size: clamp(12px, 1vw, 14px);
  color: var(--warn);
  background: rgba(255, 194, 75, 0.06);
  border: 1px solid rgba(255, 194, 75, 0.15);
  border-radius: 10px;
  padding: 8px 12px;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty {
  background: var(--white);
  border: 1px dashed var(--border-2);
  border-radius: 16px;
  padding: clamp(32px, 5vw, 48px);
  text-align: center;
  color: var(--txt-dim);
  margin-top: 16px;
}

.empty b {
  color: var(--txt);
}

/* ============================================
   TOAST
   ============================================ */
.toast-custom {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: var(--ink2);
  color: var(--white);
  border: 1px solid var(--border-2);
  padding: 14px 24px;
  border-radius: 14px;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  pointer-events: none;
  z-index: 100;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: var(--shadow-lg);
  font-size: clamp(13px, 1.1vw, 15px);
  max-width: 90vw;
  text-align: center;
}

.toast-custom.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* ============================================
   ANIMACIONES
   ============================================ */
.reveal {
  opacity: 0;
  transform: translateY(24px);
  animation: rise 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes rise {
  to {
    opacity: 1;
    transform: none;
  }
}

.dotlive {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--good);
  animation: pulse 2s infinite;
  flex-shrink: 0;
  display: inline-block;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(67, 178, 52, 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(67, 178, 52, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(67, 178, 52, 0);
  }
}

/* ============================================
   VISTA ENCUESTAS ACTUAL — espaciado
   Ritmo vertical único (24px escritorio / 16px móvil) en lugar de
   márgenes sueltos por tarjeta.
   ============================================ */
.view-actual {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
}

.actual-stack {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.view-actual .hero {
  margin-top: 0;
  gap: 24px;
}

.view-actual .empty {
  margin-top: 0;
}

.view-actual .card-custom {
  padding: clamp(20px, 2.5vw, 28px);
}

.view-actual .sec-sub {
  margin-bottom: 20px;
}

.view-actual .kpi-grid {
  gap: 20px;
}

.view-actual .contact-info-grid {
  gap: 12px 24px;
}

.view-actual .feedback-item {
  padding: 12px 0;
}

.view-actual .feedback-item:first-child {
  padding-top: 0;
}

.actual-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: clamp(16px, 2vw, 20px) clamp(16px, 2.5vw, 24px);
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
}

.toolbar-field {
  flex: 1 1 320px;
  max-width: 520px;
}

.toolbar-field label {
  margin-bottom: 8px;
}

.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

/* Texto solo para lectores de pantalla */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ---------- Botón IA: contorno degradado, fondo sutil ---------- */
.btn-ai {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px 0 14px;
  border: 1.5px solid transparent;
  border-radius: 12px;
  background: linear-gradient(var(--white), var(--white)) padding-box,
    linear-gradient(135deg, var(--secondary), var(--primary-light)) border-box;
  color: var(--brand-text);
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}

.btn-ai:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(26, 92, 201, 0.18);
}

.btn-ai:focus-visible {
  outline: 2px solid var(--primary-light);
  outline-offset: 2px;
}

.btn-ai:disabled {
  cursor: progress;
  opacity: 0.8;
}

.btn-ai-icon {
  width: 18px;
  height: 18px;
  fill: currentColor;
  color: var(--secondary);
  transition: transform 0.4s ease;
}

.btn-ai:hover:not(:disabled) .btn-ai-icon,
.btn-ai:disabled .btn-ai-icon {
  transform: rotate(18deg) scale(1.1);
}

.btn-ai:disabled .btn-ai-icon {
  animation: ai-pulse 1.2s ease-in-out infinite;
}

.btn-ai-badge {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  border-radius: 6px;
  color: var(--on-accent);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
}

/* ---------- Estado de carga del modal ---------- */
.ai-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: clamp(8px, 2vw, 16px) clamp(4px, 2vw, 24px);
}

.ai-progress-orb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  box-shadow: 0 0 0 0 rgba(67, 178, 52, 0.4);
  animation: ai-orb 2s ease-out infinite;
}

.ai-progress-orb svg {
  width: 28px;
  height: 28px;
  fill: #fff;
}

.ai-progress-title {
  margin: 0;
  font-size: clamp(16px, 1.4vw, 19px);
  font-weight: 700;
  color: var(--txt);
}

.ai-progress-desc {
  margin: 0;
  max-width: 560px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--txt-dim);
}

.ai-steps {
  list-style: none;
  margin: 0;
  padding: 16px 20px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
}

.ai-steps li {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--txt-faint);
  transition: color 0.3s ease;
}

.ai-steps li.current {
  color: var(--txt);
  font-weight: 600;
}

.ai-steps li.done {
  color: var(--txt-dim);
}

.ai-step-dot {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid var(--border-2);
}

.ai-steps li.current .ai-step-dot {
  border-color: var(--secondary);
  background: var(--secondary);
  animation: ai-pulse 1.2s ease-in-out infinite;
}

.ai-steps li.done .ai-step-dot {
  border-color: var(--secondary);
  background: var(--secondary);
}

.ai-progress-includes {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.ai-includes-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--txt-faint);
}

.ai-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.ai-chips span {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  color: var(--brand-text);
  background: var(--surface);
  border: 1px solid var(--border);
}

.ai-progress-note {
  margin: 0;
  font-size: 12px;
  color: var(--txt-faint);
}

@keyframes ai-pulse {
  50% {
    opacity: 0.45;
  }
}

@keyframes ai-orb {
  0% {
    box-shadow: 0 0 0 0 rgba(67, 178, 52, 0.4);
  }
  100% {
    box-shadow: 0 0 0 16px rgba(67, 178, 52, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .btn-ai-icon,
  .ai-progress-orb,
  .ai-steps li.current .ai-step-dot {
    animation: none;
  }
}

@media (max-width: 768px) {
  .view-actual,
  .actual-stack,
  .view-actual .hero {
    gap: 16px;
  }

  .view-actual {
    margin-top: 16px;
  }

  .toolbar-field {
    max-width: none;
  }

  .toolbar-actions {
    width: 100%;
  }

  .toolbar-actions > button {
    flex: 1 1 auto;
    justify-content: center;
  }
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 1024px) {
  .hero {
    grid-template-columns: 1fr 1fr;
  }
  .kpi-grid {
    grid-template-columns: repeat(2, 2fr);
  }
  .dir-kpis {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 768px) {
  .hero {
    grid-template-columns: 1fr;
  }
  .dir-kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 680px) {
  .resp-row {
    grid-template-columns: 1fr auto;
  }
  .resp-val,
  .exec-kpis {
    display: none;
  }
  .exec-row {
    flex-direction: column;
    align-items: stretch;
  }
  .exec-right {
    justify-content: space-between;
  }
}

@media (max-width: 480px) {
  .kpi-selector {
    min-width: 100px;
  }

  .btn-option {
    min-width: 24px;
    max-width: 30px;
    height: 28px;
    font-size: 11px;
  }

  .kpi-btn-group {
    padding: 4px;
    gap: 3px;
  }

  .tabs-custom {
    width: 100%;
  }

  .tab-custom {
    flex: 1;
    text-align: center;
    padding: 10px 12px;
    font-size: 12px;
  }

  .btn-custom {
    width: 100%;
    justify-content: center;
  }

  .filters > div {
    min-width: 140px;
    flex: 1 1 100%;
  }

  .row > * {
    min-width: 140px;
    flex: 1 1 100%;
  }

  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .dir-kpis {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 360px) {
  .btn-option {
    min-width: 20px;
    max-width: 26px;
    height: 24px;
    font-size: 10px;
  }
}

/* ============================================
   UTILITIES
   ============================================ */
.text-center {
  text-align: center;
}

.w-full {
  width: 100%;
}

.gap-2 {
  gap: 8px;
}

.flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hidden {
  display: none !important;
}

/* ============================================
   SCROLLBAR
   ============================================ */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: var(--primary);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--primary-light);
}

/* ============================================
   NUEVO: período + selección de secciones
   ============================================ */
.secciones-check {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.check-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--white);
  cursor: pointer;
  font-size: clamp(12px, 1vw, 14px);
  font-weight: 600;
  color: var(--txt-dim);
  transition: all 0.2s ease;
  user-select: none;
}

.check-chip:hover {
  border-color: var(--border-2);
  background: var(--surface);
}

.check-chip.active {
  border-color: var(--secondary);
  color: var(--txt);
  background: linear-gradient(120deg, rgba(67, 178, 52, 0.08), var(--white));
  box-shadow: 0 0 0 1px rgba(67, 178, 52, 0.25);
}

.check-chip input {
  width: auto;
  accent-color: var(--secondary);
}

/* ============================================
   NUEVO: nombre de cliente y acciones en lista histórica
   ============================================ */
.exec-client {
  font-weight: 700;
  font-size: clamp(14px, 1.2vw, 16px);
  color: var(--brand-text);
}

.exec-contacto {
  font-size: clamp(12px, 1vw, 13px);
  color: var(--txt-dim);
  font-weight: 600;
}

.exec-contacto .exec-puesto {
  color: var(--txt-faint);
  font-weight: 500;
}

.exec-actions {
  display: flex;
  gap: 6px;
}

.icon-btn {
  border: 1px solid var(--border);
  background: var(--white);
  border-radius: 10px;
  width: 34px;
  height: 34px;
  cursor: pointer;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  line-height: 1;
}

.icon-btn:hover {
  border-color: var(--border-2);
  background: var(--surface);
  transform: translateY(-1px);
}

.icon-btn.danger:hover {
  border-color: rgba(231, 76, 60, 0.4);
  background: rgba(231, 76, 60, 0.06);
}

/* ============================================
   NUEVO: spinner inline
   ============================================ */
.spinner-sm {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  vertical-align: -2px;
  margin-right: 6px;
}

.spinner-sm.dark {
  border-color: rgba(0, 60, 165, 0.25);
  border-top-color: var(--primary);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   NUEVO: modales (editar / eliminar ejecución)
   ============================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(10, 22, 40, 0.45);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
}

.modal-box {
  background: var(--white);
  border-radius: 18px;
  width: 100%;
  max-width: 720px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
  margin: auto;
  animation: rise 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
}

.modal-head h3 {
  margin: 0;
  font-size: clamp(15px, 1.4vw, 18px);
}

.modal-body {
  padding: 16px 20px;
  max-height: 65vh;
  overflow-y: auto;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--border);
}

.mode-switch {
  display: flex;
  gap: 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 4px;
  width: fit-content;
}

.mode-btn {
  font: inherit;
  font-weight: 600;
  font-size: clamp(12px, 1vw, 13px);
  color: var(--txt-dim);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 9px;
  transition: all 0.25s ease;
}

.mode-btn.active {
  color: var(--on-accent);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
}

.radio-stack {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  font-size: clamp(12px, 1vw, 14px);
  color: var(--txt-dim);
}

.radio-stack input {
  width: auto;
  margin-top: 3px;
  accent-color: var(--primary);
}

@media (max-width: 680px) {
  .exec-actions {
    order: 3;
  }
}

/* ============================================
   NUEVO: análisis ejecutivo (Markdown)
   ============================================ */
.analysis-error {
  background: rgba(255, 93, 115, 0.08);
  border: 1px solid rgba(255, 93, 115, 0.25);
  color: #b3253a;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.5;
}

.markdown-body {
  font-size: 14px;
  line-height: 1.65;
  color: var(--txt);
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4 {
  color: var(--heading);
  margin: 16px 0 8px;
  line-height: 1.3;
}

.markdown-body h1 {
  font-size: 20px;
}
.markdown-body h2 {
  font-size: 17px;
  border-bottom: 2px solid var(--border);
  padding-bottom: 5px;
}
.markdown-body h3 {
  font-size: 15px;
}

.markdown-body p {
  margin: 8px 0;
}

.markdown-body ul,
.markdown-body ol {
  margin: 8px 0;
  padding-left: 22px;
}

.markdown-body li {
  margin: 4px 0;
}

.markdown-body strong {
  color: var(--heading);
}

.markdown-body code {
  font-family: var(--mono);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 1px 5px;
  font-size: 0.9em;
}

.markdown-body .md-pre {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
  overflow-x: auto;
}

.markdown-body .md-pre code {
  border: none;
  background: none;
  padding: 0;
}

.markdown-body blockquote {
  border-left: 3px solid var(--secondary);
  margin: 8px 0;
  padding: 4px 12px;
  color: var(--txt-dim);
  background: rgba(67, 178, 52, 0.05);
  border-radius: 0 8px 8px 0;
}

.markdown-body hr {
  border: none;
  border-top: 1px solid var(--border);
  margin: 14px 0;
}

.markdown-body a {
  color: var(--brand-text);
  text-decoration: underline;
}
</style>
